import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { supabase } from '../../lib/supabase';
import { sendNewMessageNotification, sendTradeScheduledNotification, sendLocationTradeNotification, sendTradeCancelledNotification, sendRunningLateNotification, sendRescheduleRequestNotification, sendRescheduleResponseNotification, sendLateResponseNotification } from '../../lib/notifications';
import { sendTradeConfirmationEmails, sendCancellationEmails, sendRescheduleEmails } from '../../lib/emails';

// Chase character images
import ChaseHi from '../../assets/images/ChaseHi.png';
import ChaseThink from '../../assets/images/ChaseThink.png';
import ChaseThumbsUp from '../../assets/images/ChaseThumbsUp.png';

// Chase's system user ID
const CHASE_ID = '550e8400-e29b-41d4-a716-446655440000';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const StarIcon = ({ filled = false }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const CloseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

// Helper to get current user data
const getCurrentUserData = async () => {
  const { data } = await supabase.auth.getSession();
  const authId = data?.session?.user?.id;
  if (!authId) return null;

  const { data: userData, error } = await supabase
    .from('users')
    .select('id, first_name, last_name, username, avatar_url, latitude, longitude, location_city, location_state')
    .eq('auth_id', authId)
    .single();

  if (error || !userData) return null;
  return userData;
};

// Haversine formula to calculate distance between two points (in miles)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

// Format distance for display
const formatDistance = (distance) => {
  if (distance === null || distance === undefined) return null;
  if (distance < 5) return '<5 mi';
  return `${distance} mi`;
};

// Progress Step Component
const ProgressStep = ({ label, status, isLast = false }) => {
  return (
    <>
      <div style={styles.progressStep}>
        <div style={{
          ...styles.progressIcon,
          ...(status === 'completed' ? styles.progressIconCompleted :
              status === 'active' ? styles.progressIconActive :
              styles.progressIconPending),
        }}>
          {status === 'completed' ? (
            <CheckIcon size={10} />
          ) : (
            <div style={styles.progressDot} />
          )}
        </div>
        <span style={{
          ...styles.progressLabel,
          ...(status === 'active' ? styles.progressLabelActive : {}),
        }}>{label}</span>
      </div>
      {!isLast && (
        <div style={{
          ...styles.progressLine,
          ...(status === 'completed' ? styles.progressLineCompleted : {}),
        }} />
      )}
    </>
  );
};

// Chase Message Component
const ChaseMessage = ({ message, chaseImage = 'hi', showConfirmButtons, onConfirm, onDecline, confirmLabel = 'Yes', declineLabel = 'No' }) => {
  const CHASE_IMAGES = {
    hi: ChaseHi,
    think: ChaseThink,
    thumbsup: ChaseThumbsUp,
  };
  const imageSrc = CHASE_IMAGES[chaseImage] || ChaseHi;

  return (
    <div style={styles.chaseMessageContainer}>
      <img src={imageSrc} alt="Chase" style={styles.chaseAvatar} />
      <div style={styles.chaseMessageBubble}>
        <span style={styles.chaseName}>Chase</span>
        <p style={styles.chaseMessageText}>{message}</p>

        {showConfirmButtons && (
          <div style={styles.confirmButtonsRow}>
            <button style={styles.confirmYesButton} onClick={onConfirm}>
              {confirmLabel}
            </button>
            <button style={styles.confirmNoButton} onClick={onDecline}>
              {declineLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// User Message Component
const UserMessage = ({ isMe, message, partnerName }) => {
  return (
    <div style={{
      ...styles.userMessageContainer,
      ...(isMe ? styles.userMessageRight : styles.userMessageLeft),
    }}>
      <div style={{
        ...styles.userMessageBubble,
        ...(isMe ? styles.userMessageBubbleMe : styles.userMessageBubbleOther),
      }}>
        <p style={{ ...styles.userMessageText, color: isMe ? '#fff' : 'var(--text)' }}>{message}</p>
      </div>
    </div>
  );
};

// Helper to check if a location is fully blocked for next 7 days
const getLocationBlockStatus = (locationId, blockedTimes, operatingHours) => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    days.push(date);
  }

  // Get blocked times for this location
  const locationBlocks = blockedTimes.filter(bt => bt.location_id === locationId);

  // Check each of the next 7 days
  let blockedDays = 0;
  let blockReason = null;
  let blockDates = [];

  for (const day of days) {
    const dateStr = day.toISOString().split('T')[0];
    const dayName = day.toLocaleDateString('en-US', { weekday: 'long' });

    // Check if closed on this day (operating hours)
    const isClosed = operatingHours?.[dayName] === 'Closed' || !operatingHours?.[dayName];

    // Check if blocked on this date
    const dayBlock = locationBlocks.find(bt => bt.date === dateStr && bt.all_day);

    if (isClosed || dayBlock) {
      blockedDays++;
      if (dayBlock?.reason && !blockReason) {
        blockReason = dayBlock.reason;
      }
      if (dayBlock) {
        blockDates.push(day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    }
  }

  const isFullyBlocked = blockedDays === 7;
  return {
    isFullyBlocked,
    blockReason,
    blockDates: blockDates.length > 0 ? `${blockDates[0]}${blockDates.length > 1 ? ` - ${blockDates[blockDates.length - 1]}` : ''}` : null,
  };
};

// Location Selection Modal
const LocationModal = ({ isOpen, onClose, locations, selectedIds, onToggle, onSubmit, loading, currentUser, partner, blockedTimes = [] }) => {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'nearMe', 'nearTrader'
  const [tappedBlockedLocation, setTappedBlockedLocation] = useState(null);
  const FILTER_RADIUS = 50; // miles

  // Suggest location form state
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestSubmitted, setSuggestSubmitted] = useState(false);
  const [suggestTab, setSuggestTab] = useState('store'); // 'store' or 'event'
  const [storeForm, setStoreForm] = useState({
    storeName: '', address: '', city: '', state: '', zipCode: '',
    phone: '', website: '', isOwner: null, relationship: '', isNearby: null, notes: ''
  });
  const [eventForm, setEventForm] = useState({
    eventName: '', isConfirmed: null, isOrganizer: null, sourceType: '', sourceLink: '',
    frequency: null, venueName: '', address: '', city: '', state: '',
    eventDate: '', eventTime: '', description: '', expectedVendors: '', notes: ''
  });

  if (!isOpen) return null;

  // Get user locations
  const myLocation = currentUser?.latitude && currentUser?.longitude
    ? { lat: parseFloat(currentUser.latitude), lng: parseFloat(currentUser.longitude), city: currentUser.location_city, state: currentUser.location_state }
    : null;
  const traderLocation = partner?.latitude && partner?.longitude
    ? { lat: parseFloat(partner.latitude), lng: parseFloat(partner.longitude), city: partner.location_city, state: partner.location_state }
    : null;

  // Calculate distances and filter
  const processedLocations = locations
    .map(loc => {
      const locLat = parseFloat(loc.latitude);
      const locLng = parseFloat(loc.longitude);
      return {
        ...loc,
        distanceFromMe: myLocation ? calculateDistance(myLocation.lat, myLocation.lng, locLat, locLng) : null,
        distanceFromTrader: traderLocation ? calculateDistance(traderLocation.lat, traderLocation.lng, locLat, locLng) : null,
      };
    })
    .filter(loc => {
      // Only show Basic+ tiers (Free tier 0 is just listed, not selectable)
      if ((loc.subscription_tier || 0) < 1) return false;

      // Search filter
      const matchesSearch = `${loc.store_name} ${loc.city} ${loc.state}`.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;

      // Location filter
      if (filterMode === 'nearMe' && myLocation) {
        return loc.distanceFromMe !== null && loc.distanceFromMe <= FILTER_RADIUS;
      }
      if (filterMode === 'nearTrader' && traderLocation) {
        return loc.distanceFromTrader !== null && loc.distanceFromTrader <= FILTER_RADIUS;
      }
      return true;
    })
    .sort((a, b) => {
      // Tier priority: Premium (3) and Enhanced (2) get boosted
      // Premium: top priority within 30 miles
      // Enhanced: priority within 10 miles
      const aTier = a.subscription_tier || 0;
      const bTier = b.subscription_tier || 0;
      const aDistance = filterMode === 'nearTrader' ? a.distanceFromTrader : a.distanceFromMe;
      const bDistance = filterMode === 'nearTrader' ? b.distanceFromTrader : b.distanceFromMe;

      // Premium (tier 3) within 30mi goes first
      const aPremiumBoost = aTier === 3 && aDistance !== null && aDistance <= 30;
      const bPremiumBoost = bTier === 3 && bDistance !== null && bDistance <= 30;
      if (aPremiumBoost && !bPremiumBoost) return -1;
      if (!aPremiumBoost && bPremiumBoost) return 1;

      // Enhanced (tier 2) within 10mi goes next
      const aEnhancedBoost = aTier === 2 && aDistance !== null && aDistance <= 10;
      const bEnhancedBoost = bTier === 2 && bDistance !== null && bDistance <= 10;
      if (aEnhancedBoost && !bEnhancedBoost) return -1;
      if (!aEnhancedBoost && bEnhancedBoost) return 1;

      // Then sort by distance
      if (filterMode === 'nearMe' && a.distanceFromMe !== null && b.distanceFromMe !== null) {
        return a.distanceFromMe - b.distanceFromMe;
      }
      if (filterMode === 'nearTrader' && a.distanceFromTrader !== null && b.distanceFromTrader !== null) {
        return a.distanceFromTrader - b.distanceFromTrader;
      }
      return a.store_name.localeCompare(b.store_name);
    });

  // Get display distance based on filter mode
  const getDisplayDistance = (loc) => {
    if (filterMode === 'nearTrader' && loc.distanceFromTrader !== null) {
      return formatDistance(loc.distanceFromTrader);
    }
    if (loc.distanceFromMe !== null) {
      return formatDistance(loc.distanceFromMe);
    }
    return null;
  };

  // Get partner's username for display
  const traderName = partner?.username || 'Trader';

  // Get current user ID for submission
  const currentUserId = currentUser?.id;

  // Submit store suggestion
  const submitStoreSuggestion = async () => {
    if (!storeForm.storeName || !storeForm.city || !storeForm.state) return;
    setSuggestSubmitting(true);
    try {
      const { error } = await supabase
        .from('location_suggestions')
        .insert({
          submitted_by: currentUserId,
          store_name: storeForm.storeName,
          address: storeForm.address || null,
          city: storeForm.city,
          state: storeForm.state.toUpperCase(),
          zip_code: storeForm.zipCode || null,
          phone: storeForm.phone || null,
          website: storeForm.website || null,
          is_owner: storeForm.isOwner === true,
          relationship: storeForm.relationship || null,
          discovery_method: storeForm.isNearby || null,
          notes: storeForm.notes || null,
          source: 'meetup_chat',
        });
      if (error) throw error;
      setSuggestSubmitted(true);
    } catch (error) {
      console.error('Error submitting store suggestion:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  // Submit event suggestion
  const submitEventSuggestion = async () => {
    if (!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink) return;
    setSuggestSubmitting(true);
    try {
      const { error } = await supabase
        .from('event_suggestions')
        .insert({
          submitted_by: currentUserId,
          event_name: eventForm.eventName,
          is_confirmed: eventForm.isConfirmed,
          is_organizer: eventForm.isOrganizer === true,
          source_type: eventForm.sourceType || null,
          source_link: eventForm.sourceLink || null,
          frequency: eventForm.frequency || null,
          venue_name: eventForm.venueName || null,
          address: eventForm.address || null,
          city: eventForm.city,
          state: eventForm.state.toUpperCase(),
          event_date: eventForm.eventDate || null,
          event_time: eventForm.eventTime || null,
          description: eventForm.description || null,
          expected_vendors: eventForm.expectedVendors ? parseInt(eventForm.expectedVendors) : null,
          notes: eventForm.notes || null,
          source: 'meetup_chat',
        });
      if (error) throw error;
      setSuggestSubmitted(true);
    } catch (error) {
      console.error('Error submitting event suggestion:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  // Handle going back from suggest form
  const handleSuggestBack = () => {
    setShowSuggestForm(false);
    setSuggestSubmitted(false);
    setSuggestTab('store');
    setStoreForm({
      storeName: '', address: '', city: '', state: '', zipCode: '',
      phone: '', website: '', isOwner: null, relationship: '', isNearby: null, notes: ''
    });
    setEventForm({
      eventName: '', isConfirmed: null, isOrganizer: null, sourceType: '', sourceLink: '',
      frequency: null, venueName: '', address: '', city: '', state: '',
      eventDate: '', eventTime: '', description: '', expectedVendors: '', notes: ''
    });
  };

  // Suggest form styles (inline for this modal)
  const suggestStyles = {
    formScroll: { flex: 1, overflowY: 'auto', padding: '16px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
    tab: { flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid #333', backgroundColor: '#1a1a1a', color: '#999', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    tabActive: { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '6px' },
    labelSub: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#888', marginBottom: '6px' },
    required: { color: '#ef4444' },
    input: { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #333', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '15px', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #333', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '15px', boxSizing: 'border-box', resize: 'none' },
    row: { display: 'flex', gap: '12px' },
    btnRow: { display: 'flex', gap: '10px', marginBottom: '8px' },
    choiceBtn: { flex: 1, padding: '12px', borderRadius: '8px', border: '2px solid #333', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    choiceBtnActive: { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' },
    chipBtn: { padding: '8px 14px', borderRadius: '8px', border: '2px solid #333', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
    chipBtnActive: { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' },
    hint: { fontSize: '12px', color: '#888', margin: '4px 0 0 0' },
    submitBtn: { width: '100%', padding: '14px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
    submitBtnDisabled: { backgroundColor: '#2a2a2a', color: '#666', cursor: 'not-allowed' },
    successContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' },
    successIcon: { width: '60px', height: '60px', borderRadius: '50%', backgroundColor: 'rgba(34, 197, 94, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', color: '#22c55e' },
    successTitle: { fontSize: '20px', fontWeight: '700', color: '#fff', marginBottom: '12px' },
    successText: { fontSize: '14px', color: '#888', lineHeight: '1.5', marginBottom: '24px' },
    backBtn: { padding: '12px 24px', borderRadius: '10px', border: '1px solid #333', backgroundColor: 'transparent', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  };

  // Render suggest location form
  if (showSuggestForm) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modalContentCenter} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeaderRow}>
            <button style={styles.modalCloseBtn} onClick={handleSuggestBack}>
              <ChevronLeftIcon />
            </button>
            <div style={styles.modalHeaderCenter}>
              <h2 style={styles.modalTitle}>{suggestSubmitted ? 'Request Submitted!' : 'Suggest a Location'}</h2>
            </div>
            <div style={{ width: 60 }} />
          </div>

          {suggestSubmitted ? (
            <div style={suggestStyles.successContainer}>
              <div style={suggestStyles.successIcon}>
                <CheckIcon size={28} />
              </div>
              <h3 style={suggestStyles.successTitle}>We've received your request!</h3>
              <p style={suggestStyles.successText}>
                Thanks for the suggestion! Our team will review it and if approved,
                it will become available as a meetup location. This usually takes 1-2 business days.
              </p>
              <button style={suggestStyles.backBtn} onClick={handleSuggestBack}>
                Back to Locations
              </button>
            </div>
          ) : (
            <>
              {/* Store / Event Tabs */}
              <div style={{ padding: '0 16px' }}>
                <div style={suggestStyles.tabs}>
                  <button
                    style={{ ...suggestStyles.tab, ...(suggestTab === 'store' ? suggestStyles.tabActive : {}) }}
                    onClick={() => setSuggestTab('store')}
                  >
                    Store
                  </button>
                  <button
                    style={{ ...suggestStyles.tab, ...(suggestTab === 'event' ? suggestStyles.tabActive : {}) }}
                    onClick={() => setSuggestTab('event')}
                  >
                    Event
                  </button>
                </div>
              </div>

              <div style={suggestStyles.formScroll}>
                {suggestTab === 'store' ? (
                  /* Store Form */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Store Name */}
                    <div>
                      <label style={suggestStyles.label}>Store Name <span style={suggestStyles.required}>*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Epic Card Games"
                        value={storeForm.storeName}
                        onChange={(e) => setStoreForm(prev => ({ ...prev, storeName: e.target.value }))}
                        style={suggestStyles.input}
                      />
                    </div>

                    {/* Are you the owner? */}
                    <div>
                      <label style={suggestStyles.label}>Are you the owner of this store? <span style={suggestStyles.required}>*</span></label>
                      <div style={suggestStyles.btnRow}>
                        <button
                          type="button"
                          onClick={() => setStoreForm(prev => ({ ...prev, isOwner: true, relationship: '', isNearby: null }))}
                          style={{ ...suggestStyles.choiceBtn, ...(storeForm.isOwner === true ? suggestStyles.choiceBtnActive : {}) }}
                        >
                          Yes, I'm the owner
                        </button>
                        <button
                          type="button"
                          onClick={() => setStoreForm(prev => ({ ...prev, isOwner: false }))}
                          style={{ ...suggestStyles.choiceBtn, ...(storeForm.isOwner === false ? suggestStyles.choiceBtnActive : {}) }}
                        >
                          No
                        </button>
                      </div>

                      {storeForm.isOwner === false && (
                        <div style={{ marginTop: '12px' }}>
                          <label style={suggestStyles.labelSub}>How do you know this store?</label>
                          <select
                            value={storeForm.relationship}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, relationship: e.target.value }))}
                            style={suggestStyles.input}
                          >
                            <option value="">Select...</option>
                            <option value="customer">I'm a customer</option>
                            <option value="employee">I work there</option>
                            <option value="friend">I know the owner</option>
                            <option value="visited">I've visited before</option>
                            <option value="heard">I've heard about it</option>
                            <option value="other">Other</option>
                          </select>

                          <div style={{ marginTop: '12px' }}>
                            <label style={suggestStyles.labelSub}>How did you find this store?</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              <button type="button" onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'local' }))} style={{ ...suggestStyles.chipBtn, ...(storeForm.isNearby === 'local' ? suggestStyles.chipBtnActive : {}) }}>It's local to me</button>
                              <button type="button" onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'traveling' }))} style={{ ...suggestStyles.chipBtn, ...(storeForm.isNearby === 'traveling' ? suggestStyles.chipBtnActive : {}) }}>Found while traveling</button>
                              <button type="button" onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'online' }))} style={{ ...suggestStyles.chipBtn, ...(storeForm.isNearby === 'online' ? suggestStyles.chipBtnActive : {}) }}>Looked it up online</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Address */}
                    <div>
                      <label style={suggestStyles.label}>Address</label>
                      <input type="text" placeholder="123 Main St" value={storeForm.address} onChange={(e) => setStoreForm(prev => ({ ...prev, address: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* City & State */}
                    <div style={suggestStyles.row}>
                      <div style={{ flex: 2 }}>
                        <label style={suggestStyles.label}>City <span style={suggestStyles.required}>*</span></label>
                        <input type="text" placeholder="Austin" value={storeForm.city} onChange={(e) => setStoreForm(prev => ({ ...prev, city: e.target.value }))} style={suggestStyles.input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>State <span style={suggestStyles.required}>*</span></label>
                        <input type="text" placeholder="TX" maxLength={2} value={storeForm.state} onChange={(e) => setStoreForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))} style={suggestStyles.input} />
                      </div>
                    </div>

                    {/* Zip & Phone */}
                    <div style={suggestStyles.row}>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>Zip Code</label>
                        <input type="text" placeholder="78701" value={storeForm.zipCode} onChange={(e) => setStoreForm(prev => ({ ...prev, zipCode: e.target.value }))} style={suggestStyles.input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>Phone</label>
                        <input type="tel" placeholder="(555) 123-4567" value={storeForm.phone} onChange={(e) => setStoreForm(prev => ({ ...prev, phone: e.target.value }))} style={suggestStyles.input} />
                      </div>
                    </div>

                    {/* Website */}
                    <div>
                      <label style={suggestStyles.label}>Website</label>
                      <input type="url" placeholder="https://..." value={storeForm.website} onChange={(e) => setStoreForm(prev => ({ ...prev, website: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* Notes */}
                    <div>
                      <label style={suggestStyles.label}>Additional Notes</label>
                      <textarea placeholder="Why should we add this store?" value={storeForm.notes} onChange={(e) => setStoreForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} style={suggestStyles.textarea} />
                    </div>

                    {/* Submit */}
                    <button
                      onClick={submitStoreSuggestion}
                      disabled={!storeForm.storeName || !storeForm.city || !storeForm.state || suggestSubmitting}
                      style={{ ...suggestStyles.submitBtn, ...(!storeForm.storeName || !storeForm.city || !storeForm.state ? suggestStyles.submitBtnDisabled : {}) }}
                    >
                      {suggestSubmitting ? 'Submitting...' : 'Submit Store Suggestion'}
                    </button>
                  </div>
                ) : (
                  /* Event Form */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* 1. Are you the organizer? */}
                    <div>
                      <label style={suggestStyles.label}>Are you the organizer of this event? <span style={suggestStyles.required}>*</span></label>
                      <div style={suggestStyles.btnRow}>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, isOrganizer: true, sourceType: '' }))} style={{ ...suggestStyles.choiceBtn, ...(eventForm.isOrganizer === true ? suggestStyles.choiceBtnActive : {}) }}>Yes, I'm the organizer</button>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, isOrganizer: false }))} style={{ ...suggestStyles.choiceBtn, ...(eventForm.isOrganizer === false ? suggestStyles.choiceBtnActive : {}) }}>No</button>
                      </div>
                      {eventForm.isOrganizer === false && (
                        <div style={{ marginTop: '12px' }}>
                          <label style={suggestStyles.labelSub}>How did you hear about it?</label>
                          <input type="text" placeholder="Facebook, Instagram, a friend, etc." value={eventForm.sourceType} onChange={(e) => setEventForm(prev => ({ ...prev, sourceType: e.target.value }))} style={suggestStyles.input} />
                        </div>
                      )}
                    </div>

                    {/* 2. Is this officially scheduled? */}
                    <div>
                      <label style={suggestStyles.label}>Is this event officially scheduled? <span style={suggestStyles.required}>*</span></label>
                      <div style={suggestStyles.btnRow}>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, isConfirmed: true }))} style={{ ...suggestStyles.choiceBtn, ...(eventForm.isConfirmed === true ? suggestStyles.choiceBtnActive : {}) }}>Yes, it's confirmed</button>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, isConfirmed: false }))} style={{ ...suggestStyles.choiceBtn, ...(eventForm.isConfirmed === false ? suggestStyles.choiceBtnActive : {}) }}>Not sure</button>
                      </div>
                    </div>

                    {/* 3. Event Name */}
                    <div>
                      <label style={suggestStyles.label}>Event Name <span style={suggestStyles.required}>*</span></label>
                      <input type="text" placeholder="e.g. Pokemon Card Show" value={eventForm.eventName} onChange={(e) => setEventForm(prev => ({ ...prev, eventName: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* Link to event page */}
                    <div>
                      <label style={suggestStyles.label}>Link to event page <span style={suggestStyles.required}>*</span></label>
                      <input type="url" placeholder="https://facebook.com/events/..." value={eventForm.sourceLink} onChange={(e) => setEventForm(prev => ({ ...prev, sourceLink: e.target.value }))} style={suggestStyles.input} />
                      <p style={suggestStyles.hint}>Facebook, Instagram, website, etc.</p>
                    </div>

                    {/* Frequency */}
                    <div>
                      <label style={suggestStyles.label}>How often is this event held?</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, frequency: 'weekly' }))} style={{ ...suggestStyles.chipBtn, ...(eventForm.frequency === 'weekly' ? suggestStyles.chipBtnActive : {}) }}>Weekly</button>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, frequency: 'monthly' }))} style={{ ...suggestStyles.chipBtn, ...(eventForm.frequency === 'monthly' ? suggestStyles.chipBtnActive : {}) }}>Monthly</button>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, frequency: 'regular' }))} style={{ ...suggestStyles.chipBtn, ...(eventForm.frequency === 'regular' ? suggestStyles.chipBtnActive : {}) }}>Regular schedule</button>
                        <button type="button" onClick={() => setEventForm(prev => ({ ...prev, frequency: 'irregular' }))} style={{ ...suggestStyles.chipBtn, ...(eventForm.frequency === 'irregular' ? suggestStyles.chipBtnActive : {}) }}>One-time / Irregular</button>
                      </div>
                    </div>

                    {/* Venue */}
                    <div>
                      <label style={suggestStyles.label}>Venue / Location Name</label>
                      <input type="text" placeholder="e.g. Convention Center" value={eventForm.venueName} onChange={(e) => setEventForm(prev => ({ ...prev, venueName: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* Address */}
                    <div>
                      <label style={suggestStyles.label}>Address</label>
                      <input type="text" placeholder="123 Main St" value={eventForm.address} onChange={(e) => setEventForm(prev => ({ ...prev, address: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* City & State */}
                    <div style={suggestStyles.row}>
                      <div style={{ flex: 2 }}>
                        <label style={suggestStyles.label}>City <span style={suggestStyles.required}>*</span></label>
                        <input type="text" placeholder="Austin" value={eventForm.city} onChange={(e) => setEventForm(prev => ({ ...prev, city: e.target.value }))} style={suggestStyles.input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>State <span style={suggestStyles.required}>*</span></label>
                        <input type="text" placeholder="TX" maxLength={2} value={eventForm.state} onChange={(e) => setEventForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))} style={suggestStyles.input} />
                      </div>
                    </div>

                    {/* Date & Time */}
                    <div style={suggestStyles.row}>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>Event Date</label>
                        <input type="date" value={eventForm.eventDate} onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))} style={suggestStyles.input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={suggestStyles.label}>Event Time</label>
                        <input type="text" placeholder="10am - 4pm" value={eventForm.eventTime} onChange={(e) => setEventForm(prev => ({ ...prev, eventTime: e.target.value }))} style={suggestStyles.input} />
                      </div>
                    </div>
                    <p style={{ ...suggestStyles.hint, marginTop: '-12px' }}>If multiple days, just put the first day and we'll confirm the rest.</p>

                    {/* Description */}
                    <div>
                      <label style={suggestStyles.label}>Description</label>
                      <textarea placeholder="Tell us about the event - trading, vendors, card show, etc. More details = better chance of approval!" value={eventForm.description} onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))} rows={3} style={suggestStyles.textarea} />
                    </div>

                    {/* Expected Vendors */}
                    <div>
                      <label style={suggestStyles.label}>Approx. Number of Vendors (if any)</label>
                      <input type="number" placeholder="e.g. 20" value={eventForm.expectedVendors} onChange={(e) => setEventForm(prev => ({ ...prev, expectedVendors: e.target.value }))} style={suggestStyles.input} />
                    </div>

                    {/* Notes */}
                    <div>
                      <label style={suggestStyles.label}>Additional Notes</label>
                      <textarea placeholder="Anything else we should know..." value={eventForm.notes} onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} style={suggestStyles.textarea} />
                    </div>

                    {/* Submit */}
                    <button
                      onClick={submitEventSuggestion}
                      disabled={!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink || suggestSubmitting}
                      style={{ ...suggestStyles.submitBtn, ...(!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink ? suggestStyles.submitBtnDisabled : {}) }}
                    >
                      {suggestSubmitting ? 'Submitting...' : 'Submit Event Suggestion'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContentCenter} onClick={(e) => e.stopPropagation()}>
        {/* Header with close and submit */}
        <div style={styles.modalHeaderRow}>
          <button style={styles.modalCloseBtn} onClick={onClose}>
            <CloseIcon />
          </button>
          <div style={styles.modalHeaderCenter}>
            <h2 style={styles.modalTitle}>Select Locations</h2>
            <span style={styles.selectedCountSmall}>{selectedIds.length}/3 selected</span>
          </div>
          <button
            style={{
              ...styles.submitBtnSmall,
              ...(selectedIds.length === 0 || loading ? styles.submitBtnSmallDisabled : {}),
            }}
            onClick={onSubmit}
            disabled={selectedIds.length === 0 || loading}
          >
            {loading ? '...' : 'Done'}
          </button>
        </div>

        {/* Location Filter Toggle */}
        <div style={styles.locationFilterContainer}>
          <button
            style={{
              ...styles.locationFilterBtn,
              ...(filterMode === 'nearMe' ? styles.locationFilterBtnActive : {}),
              ...(!myLocation ? styles.locationFilterBtnDisabled : {}),
            }}
            onClick={() => myLocation && setFilterMode(filterMode === 'nearMe' ? 'all' : 'nearMe')}
            disabled={!myLocation}
          >
            Near Me
          </button>
          <button
            style={{
              ...styles.locationFilterBtn,
              ...(filterMode === 'nearTrader' ? styles.locationFilterBtnActive : {}),
              ...(!traderLocation ? styles.locationFilterBtnDisabled : {}),
            }}
            onClick={() => traderLocation && setFilterMode(filterMode === 'nearTrader' ? 'all' : 'nearTrader')}
            disabled={!traderLocation}
          >
            Near {traderName}
          </button>
        </div>

        {/* Info banner when filter is active */}
        {filterMode !== 'all' && (
          <div style={styles.locationFilterBanner}>
            <span>
              {filterMode === 'nearMe'
                ? `Within ${FILTER_RADIUS} mi of ${myLocation?.city || 'you'}${myLocation?.state ? `, ${myLocation.state}` : ''}`
                : `Within ${FILTER_RADIUS} mi of ${traderLocation?.city || traderName}${traderLocation?.state ? `, ${traderLocation.state}` : ''}`
              }
            </span>
          </div>
        )}

        <div style={styles.searchBox}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search locations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={styles.locationList}>
          {processedLocations.map(loc => {
            const isSelected = selectedIds.includes(loc.id);
            const displayDist = getDisplayDistance(loc);
            const blockStatus = getLocationBlockStatus(loc.id, blockedTimes, loc.operating_hours);
            const isFullyBlocked = blockStatus.isFullyBlocked;

            return (
              <div
                key={loc.id}
                style={{
                  ...styles.locationItem,
                  borderLeftColor: loc.subscription_tier === 3 ? '#f59e0b' : loc.subscription_tier === 2 ? '#22c55e' : 'transparent',
                  borderLeftWidth: loc.subscription_tier >= 2 ? '3px' : '0px',
                  ...(isFullyBlocked ? { opacity: 0.5 } : {}),
                }}
                onClick={() => isFullyBlocked && setTappedBlockedLocation(loc.id === tappedBlockedLocation ? null : loc.id)}
              >
                <div style={styles.locationInfo}>
                  <div style={styles.locationNameRow}>
                    <span style={styles.locationName}>{loc.store_name}</span>
                    {loc.subscription_tier >= 2 && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: loc.subscription_tier === 3 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                        color: loc.subscription_tier === 3 ? '#f59e0b' : '#22c55e',
                        marginLeft: '6px',
                      }}>
                        {loc.subscription_tier === 3 ? 'PREMIUM' : 'VERIFIED'}
                      </span>
                    )}
                    {displayDist && (
                      <span style={styles.locationDistance}>{displayDist}</span>
                    )}
                  </div>
                  <span style={styles.locationCity}>{loc.city}, {loc.state}</span>
                  {/* Show blocked reason if tapped or always visible */}
                  {isFullyBlocked && (
                    <div style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      <span>{blockStatus.blockReason || 'Unavailable'}</span>
                      {blockStatus.blockDates && <span>({blockStatus.blockDates})</span>}
                    </div>
                  )}
                  <div style={styles.locationMeta}>
                    {loc.rating && (
                      <span style={styles.locationRating}>
                        <StarIcon filled /> {loc.rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
                {isFullyBlocked ? (
                  <span style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6a6a6a',
                    backgroundColor: '#2a2a2a',
                  }}>
                    Unavailable
                  </span>
                ) : (
                  <button
                    style={{
                      ...styles.locationSelectBtn,
                      ...(isSelected ? styles.locationSelectBtnActive : {}),
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(loc.id);
                    }}
                    disabled={!isSelected && selectedIds.length >= 3}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </button>
                )}
              </div>
            );
          })}
          {processedLocations.length === 0 && (
            <p style={styles.noResults}>
              {filterMode !== 'all'
                ? `No locations within ${FILTER_RADIUS} miles`
                : 'No locations found'}
            </p>
          )}

          {/* Suggest location link */}
          <button
            style={styles.suggestLocationLink}
            onClick={() => setShowSuggestForm(true)}
          >
            Can't find your location? Suggest one
          </button>
          <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', margin: '8px 0 0', lineHeight: 1.4 }}>
            More locations coming soon. Know a shop that wants to join?{' '}
            <span
              style={{ color: '#0ea5e9', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); window.location.href = '/locations'; }}
            >
              Join the waiting list
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper to get next 7 days
const getNextSevenDays = () => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    days.push({
      date: `${date.getMonth() + 1}/${date.getDate()}`,
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      fullDate: date,
    });
  }
  return days;
};

// Time slots - all possible slots
const ALL_TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
  '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM',
];

// For backwards compatibility
const TIME_SLOTS = ALL_TIME_SLOTS;

// Helper to convert "9:00 AM" to 24hr number (9)
const timeToHour = (timeStr) => {
  const [time, period] = timeStr.split(' ');
  let [hours] = time.split(':').map(Number);
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours;
};

// Helper to parse operating hours like "9:00 AM - 5:00 PM"
const parseOperatingHours = (hoursStr) => {
  if (!hoursStr || hoursStr === 'Closed') return null;
  const match = hoursStr.match(/(\d{1,2}:\d{2}) (AM|PM) - (\d{1,2}:\d{2}) (AM|PM)/);
  if (!match) return null;
  const openHour = timeToHour(`${match[1]} ${match[2]}`);
  const closeHour = timeToHour(`${match[3]} ${match[4]}`);
  return { open: openHour, close: closeHour };
};

// Schedule Selection Modal
const ScheduleModal = ({
  isOpen, onClose, selectedSlots, onToggle, onSelectAllDay, onSelectAll, onClearAll, onSubmit, loading,
  operatingHours = null, blockedTimes = [], locationId = null, hideSelectAll = false
}) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const days = getNextSevenDays();

  if (!isOpen) return null;

  // Check if a day is closed (operating hours)
  const isDayClosed = (day) => {
    if (!operatingHours) return false;
    const dayName = day.fullDate.toLocaleDateString('en-US', { weekday: 'long' });
    const hours = operatingHours[dayName];
    return !hours || hours === 'Closed';
  };

  // Check if a day is blocked (blocked times)
  const getDayBlockInfo = (day) => {
    if (!locationId) return null;
    const dateStr = day.fullDate.toISOString().split('T')[0];
    const block = blockedTimes.find(bt =>
      bt.location_id === locationId &&
      bt.date === dateStr &&
      bt.all_day
    );
    return block;
  };

  // Get available time slots for a day based on operating hours
  const getAvailableTimesForDay = (day) => {
    if (!operatingHours) return ALL_TIME_SLOTS;
    const dayName = day.fullDate.toLocaleDateString('en-US', { weekday: 'long' });
    const hours = parseOperatingHours(operatingHours[dayName]);
    if (!hours) return [];
    return ALL_TIME_SLOTS.filter(slot => {
      const slotHour = timeToHour(slot);
      return slotHour >= hours.open && slotHour < hours.close;
    });
  };

  // Check if a specific time is blocked
  const getTimeBlockInfo = (day, time) => {
    if (!locationId) return null;
    const dateStr = day.fullDate.toISOString().split('T')[0];
    const slotHour = timeToHour(time);
    return blockedTimes.find(bt => {
      if (bt.location_id !== locationId) return false;
      if (bt.date !== dateStr) return false;
      if (bt.all_day) return true;
      // Check time range
      if (bt.start_time && bt.end_time) {
        const startHour = parseInt(bt.start_time.split(':')[0]);
        const endHour = parseInt(bt.end_time.split(':')[0]);
        return slotHour >= startHour && slotHour < endHour;
      }
      return false;
    });
  };

  const getSelectedTimesForDay = (date) => {
    return selectedSlots.filter(s => s.date === date).map(s => s.time);
  };

  const isTimeSelected = (date, time) => {
    return selectedSlots.some(s => s.date === date && s.time === time);
  };

  const getAvailableDays = () => days.filter(day => !isDayClosed(day) && !getDayBlockInfo(day));

  const isDayFullySelected = (date) => {
    const day = days.find(d => d.date === date);
    if (!day) return false;
    const availableTimes = getAvailableTimesForDay(day).filter(t => !getTimeBlockInfo(day, t));
    const selected = getSelectedTimesForDay(date);
    return availableTimes.length > 0 && selected.length === availableTimes.length;
  };

  const isAllSelected = () => {
    const availableDays = getAvailableDays();
    if (availableDays.length === 0) return false;
    return availableDays.every(day => isDayFullySelected(day.date));
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContentCenter} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.modalHeaderRow}>
          <button style={styles.modalCloseBtn} onClick={onClose}>
            <CloseIcon />
          </button>
          <div style={styles.modalHeaderCenter}>
            <h2 style={styles.modalTitle}>Select Availability</h2>
            <span style={styles.selectedCountSmall}>{selectedSlots.length} time slot(s)</span>
          </div>
          <button
            style={{
              ...styles.submitBtnSmall,
              ...(selectedSlots.length === 0 || loading ? styles.submitBtnSmallDisabled : {}),
            }}
            onClick={onSubmit}
            disabled={selectedSlots.length === 0 || loading}
          >
            {loading ? '...' : 'Done'}
          </button>
        </div>

        {/* Select All / Clear All Row */}
        {!hideSelectAll && (
          <div style={styles.selectAllRow}>
            <button
              style={{
                ...styles.selectAllBtn,
                ...(isAllSelected() ? styles.selectAllBtnActive : {}),
              }}
              onClick={onSelectAll}
            >
              {isAllSelected() ? 'All Selected' : 'Select All Days'}
            </button>
            {selectedSlots.length > 0 && (
              <button style={styles.clearAllBtn} onClick={onClearAll}>
                Clear All
              </button>
            )}
          </div>
        )}

        {/* Day Tabs */}
        <div style={styles.dayTabs}>
          {days.map(day => {
            const isClosed = isDayClosed(day);
            const blockInfo = getDayBlockInfo(day);
            const isUnavailable = isClosed || blockInfo;

            return (
              <button
                key={day.date}
                style={{
                  ...styles.dayTab,
                  ...(selectedDay === day.date ? styles.dayTabActive : {}),
                  ...(isUnavailable ? { opacity: 0.4 } : {}),
                }}
                onClick={() => !isUnavailable && setSelectedDay(day.date)}
                disabled={isUnavailable}
              >
                <span style={styles.dayTabDay}>{day.dayName}</span>
                <span style={styles.dayTabDate}>{day.date}</span>
                {isUnavailable ? (
                  <div style={{
                    fontSize: '8px',
                    color: '#ef4444',
                    marginTop: '2px',
                    maxWidth: '50px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {blockInfo?.reason || 'Closed'}
                  </div>
                ) : getSelectedTimesForDay(day.date).length > 0 && (
                  <div style={styles.dayTabBadge}>
                    {getSelectedTimesForDay(day.date).length}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Time Slots */}
        {selectedDay ? (() => {
          const day = days.find(d => d.date === selectedDay);
          const availableTimes = day ? getAvailableTimesForDay(day) : ALL_TIME_SLOTS;

          return (
            <div style={styles.timeSlotContainer}>
              {/* Select All Day Button */}
              {!hideSelectAll && (
                <button
                  style={{
                    ...styles.selectAllDayBtn,
                    ...(isDayFullySelected(selectedDay) ? styles.selectAllDayBtnActive : {}),
                  }}
                  onClick={() => onSelectAllDay(selectedDay)}
                >
                  {isDayFullySelected(selectedDay) ? 'All Times Selected' : 'Select All Times'}
                </button>
              )}
              <div style={styles.timeSlotGrid}>
                {ALL_TIME_SLOTS.map(time => {
                  const selected = isTimeSelected(selectedDay, time);
                  const isAvailable = availableTimes.includes(time);
                  const blockInfo = day ? getTimeBlockInfo(day, time) : null;
                  const isBlocked = !!blockInfo;

                  // Check if slot is too soon (must be at least 1hr from now for today)
                  let isTooSoon = false;
                  if (day) {
                    const now = new Date();
                    const isToday = day.fullDate.toDateString() === now.toDateString();
                    if (isToday) {
                      const slotHour = timeToHour(time);
                      const minHour = now.getHours() + 1; // at least 1 hour out
                      isTooSoon = slotHour <= minHour;
                    }
                  }

                  if (isTooSoon) {
                    return (
                      <div
                        key={time}
                        style={{
                          ...styles.timeSlotBtn,
                          opacity: 0.3,
                          cursor: 'not-allowed',
                          backgroundColor: '#1a1a1a',
                        }}
                      >
                        {time}
                      </div>
                    );
                  }

                  if (!isAvailable) {
                    return (
                      <div
                        key={time}
                        style={{
                          ...styles.timeSlotBtn,
                          opacity: 0.3,
                          cursor: 'not-allowed',
                          backgroundColor: '#1a1a1a',
                        }}
                      >
                        {time}
                      </div>
                    );
                  }

                  if (isBlocked) {
                    return (
                      <div
                        key={time}
                        style={{
                          ...styles.timeSlotBtn,
                          opacity: 0.5,
                          cursor: 'not-allowed',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderColor: 'rgba(239, 68, 68, 0.3)',
                        }}
                        title={blockInfo?.reason || 'Blocked'}
                      >
                        <span>{time}</span>
                        {blockInfo?.reason && (
                          <span style={{ fontSize: '9px', color: '#ef4444', display: 'block' }}>
                            {blockInfo.reason}
                          </span>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={time}
                      style={{
                        ...styles.timeSlotBtn,
                        ...(selected ? styles.timeSlotBtnActive : {}),
                      }}
                      onClick={() => onToggle(selectedDay, time)}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })() : (
          <div style={styles.selectDayPrompt}>
            <CalendarIcon />
            <span>Select a day above to choose times</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Trade Summary Modal - shows snapshot of scheduled trade
const TradeSummaryModal = ({ isOpen, onClose, trade, schedule, locations }) => {
  if (!isOpen || !trade || !schedule) return null;

  const location = locations?.find(l => l.id === schedule.location_id);
  const locName = location ? location.store_name : 'TBD';
  const locCity = location ? `${location.city}, ${location.state}` : '';

  // Get both users
  const cardOwner = trade.card_owner || {};
  const requester = trade.requester || {};

  // Get card(s) - could be single card or array
  const cards = Array.isArray(trade.cards) ? trade.cards : trade.cards ? [trade.cards] : [];

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.summaryModal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.summaryHeader}>
          <button style={styles.modalCloseBtn} onClick={onClose}>
            <CloseIcon />
          </button>
          <h2 style={styles.summaryTitle}>Trade Scheduled</h2>
          <div style={{ width: 32 }} />
        </div>

        {/* Scheduled Badge */}
        <div style={styles.scheduledBadge}>
          <CalendarIcon />
          <span>SCHEDULED</span>
        </div>

        {/* Users Row */}
        <div style={styles.usersRow}>
          <div style={styles.userCard}>
            <div style={styles.userAvatar}>
              {cardOwner.avatar_url ? (
                <img src={cardOwner.avatar_url} alt="" style={styles.avatarImg} />
              ) : (
                <span style={styles.avatarInitial}>
                  {(cardOwner.username || 'O')[0].toUpperCase()}
                </span>
              )}
            </div>
            <span style={styles.userName}>
              {cardOwner.username || 'Owner'}
            </span>
            <span style={styles.userRole}>Card Owner</span>
          </div>

          <div style={styles.withBadge}>
            <span>WITH</span>
          </div>

          <div style={styles.userCard}>
            <div style={styles.userAvatar}>
              {requester.avatar_url ? (
                <img src={requester.avatar_url} alt="" style={styles.avatarImg} />
              ) : (
                <span style={styles.avatarInitial}>
                  {(requester.username || 'R')[0].toUpperCase()}
                </span>
              )}
            </div>
            <span style={styles.userName}>
              {requester.username || 'Requester'}
            </span>
            <span style={styles.userRole}>Requester</span>
          </div>
        </div>

        {/* Cards Section */}
        {cards.length > 0 && (
          <div style={styles.cardsSection}>
            <h3 style={styles.sectionLabel}>Card{cards.length > 1 ? 's' : ''} of Interest</h3>
            <div style={styles.cardsList}>
              {cards.map(card => (
                <div key={card.id} style={styles.cardItem}>
                  {card.digital_image_url && (
                    <img src={card.digital_image_url} alt={card.name} style={styles.cardThumb} />
                  )}
                  <span style={styles.cardName}>{card.name || 'Unknown Card'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Details Section */}
        <div style={styles.detailsSection}>
          <div style={styles.detailRow}>
            <MapPinIcon />
            <div style={styles.detailInfo}>
              <span style={styles.detailLabel}>Location</span>
              <span style={styles.detailValue}>{locName}</span>
              {locCity && <span style={styles.detailSub}>{locCity}</span>}
            </div>
          </div>

          <div style={styles.detailRow}>
            <CalendarIcon />
            <div style={styles.detailInfo}>
              <span style={styles.detailLabel}>Date</span>
              <span style={styles.detailValue}>{schedule.selected_date || 'TBD'}</span>
            </div>
          </div>

          <div style={styles.detailRow}>
            <ClockIcon />
            <div style={styles.detailInfo}>
              <span style={styles.detailLabel}>Time</span>
              <span style={styles.detailValue}>{schedule.selected_time || 'TBD'}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.summaryFooter}>
          <span style={styles.footerText}>Good luck with your trade!</span>
        </div>
      </div>
    </div>
  );
};

// Main Chat Component
const Chat = () => {
  const navigate = useNavigate();
  const { tradeId } = useParams();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const messagesEndRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [trade, setTrade] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [messages, setMessages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState([]); // Array of {date, time}
  const [submitting, setSubmitting] = useState(false);

  // Choice mode - for when card owner needs to pick from multiple matches
  const [showLocationChoice, setShowLocationChoice] = useState(false);
  const [matchedLocations, setMatchedLocations] = useState([]);
  const [chosenLocationId, setChosenLocationId] = useState(null);
  const [showScheduleChoice, setShowScheduleChoice] = useState(false);
  const [matchedTimes, setMatchedTimes] = useState([]);
  const [chosenTimeIdx, setChosenTimeIdx] = useState(null);
  const [choiceActiveDate, setChoiceActiveDate] = useState(null);

  // Trade summary modal
  const [showTradeSummary, setShowTradeSummary] = useState(false);

  // Restart scheduling confirmation
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  // Cancel trade confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Cancel/Reschedule/Late action menu
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showLateOptions, setShowLateOptions] = useState(false);
  const [pendingLateMinutes, setPendingLateMinutes] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);

  // Trade Details / Interests
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [myInterests, setMyInterests] = useState([]);
  const [partnerInterests, setPartnerInterests] = useState([]);
  const [showBinderPicker, setShowBinderPicker] = useState(false);
  const [partnerCards, setPartnerCards] = useState([]);
  const [pendingSelections, setPendingSelections] = useState([]);
  const [binderSearch, setBinderSearch] = useState('');

  // Scroll to bottom when messages change or on initial load
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages, loading]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get current user from activeProfile (supports kid identity when parent views as kid)
        if (!activeProfile) {
          setError('Could not load user data');
          setLoading(false);
          return;
        }
        setCurrentUser(activeProfile);

        // Fetch trade request
        const { data: tradeData, error: tradeError } = await supabase
          .from('trade_requests')
          .select(`
            id,
            requester_id,
            card_owner_id,
            status,
            created_at,
            accepted_at,
            cards (id, name, digital_image_url),
            requester:users!trade_requests_requester_id_fkey (id, username, avatar_url, location_city, location_state),
            card_owner:users!trade_requests_card_owner_id_fkey (id, username, avatar_url, location_city, location_state)
          `)
          .eq('id', tradeId)
          .single();

        if (tradeError || !tradeData) {
          setError('Trade not found');
          setLoading(false);
          return;
        }

        // Check if trade is in a valid chat state
        if (!['accepted', 'completed', 'cancelled'].includes(tradeData.status)) {
          setError('This trade is not yet accepted');
          setLoading(false);
          return;
        }

        const isCardOwner = tradeData.card_owner_id === activeProfile.id;
        const partner = isCardOwner ? tradeData.requester : tradeData.card_owner;
        const partnerId = partner?.id;

        // Fetch ALL accepted trades between these users (grouped cards)
        let allCards = tradeData.cards ? [tradeData.cards] : [];
        let partnerTrades = [];
        if (partnerId) {
          const { data: groupedTrades, error: groupError } = await supabase
            .from('trade_requests')
            .select('id, requester_id, card_owner_id, cards (id, name, digital_image_url)')
            .in('status', ['accepted', 'completed', 'cancelled'])
            .or(`requester_id.eq.${activeProfile.id},card_owner_id.eq.${activeProfile.id}`);

          if (!groupError && groupedTrades && groupedTrades.length > 0) {
            // Filter to only trades with this specific partner
            partnerTrades = groupedTrades.filter(t =>
              (t.requester_id === partnerId || t.card_owner_id === partnerId)
            );
            if (partnerTrades.length > 0) {
              allCards = partnerTrades
                .filter(t => t.cards)
                .map(t => t.cards);
            }
          }
        }
        console.log('All cards for trade:', allCards);

        // Calculate if trade is expired (past 72 hours since accepted)
        const hoursSinceAccepted = tradeData.accepted_at
          ? (Date.now() - new Date(tradeData.accepted_at)) / (1000 * 60 * 60)
          : 0;
        const isExpired = hoursSinceAccepted > 72;

        // Get all trade IDs in this group, SORTED for consistency
        const groupedTradeIds = partnerTrades.length > 0
          ? partnerTrades.map(t => t.id).sort()
          : [tradeId];

        setTrade({
          ...tradeData,
          isCardOwner,
          partner,
          partnerName: partner?.username || 'Trader',
          cards: allCards, // All grouped cards
          isExpired, // Whether trade is past 72 hours
          groupedTradeIds, // All trade IDs in this group
        });

        // The PRIMARY trade is always the first one when sorted (consistent across all views)
        const primaryTradeId = groupedTradeIds[0];

        // If we're not on the primary trade, redirect to it
        if (tradeId !== primaryTradeId) {
          console.log('Redirecting to primary trade:', primaryTradeId);
          navigate(`/chat/${primaryTradeId}`, { replace: true });
          return;
        }

        // Fetch schedule for the primary trade
        let { data: scheduleData } = await supabase
          .from('trade_schedules')
          .select('*')
          .eq('trade_id', primaryTradeId)
          .single();

        if (!scheduleData) {
          // Create new schedule for the PRIMARY trade
          const { data: newSchedule, error: scheduleError } = await supabase
            .from('trade_schedules')
            .insert({
              trade_id: primaryTradeId,
              status: 'pending_location',
            })
            .select()
            .single();

          if (!scheduleError) {
            scheduleData = newSchedule;
          }
        }
        setSchedule(scheduleData);

        // Fetch messages (Chase intro is created in Requests.js on accept)
        await fetchMessages();

        // Mark chat as read for current user (ignore errors - non-critical)
        try {
          const readColumn = isCardOwner ? 'last_read_owner_at' : 'last_read_requester_at';
          await supabase
            .from('trade_requests')
            .update({ [readColumn]: new Date().toISOString() })
            .eq('id', tradeId);
        } catch (readErr) {
          console.log('Could not mark chat as read:', readErr);
        }

        // Fetch locations
        const { data: locData } = await supabase
          .from('locations')
          .select('*')
          .order('store_name');
        setLocations(locData || []);

        // Fetch blocked times for all locations (next 30 days)
        const today = new Date();
        const futureDate = new Date(today);
        futureDate.setDate(today.getDate() + 30);
        const { data: blockedData } = await supabase
          .from('location_blocked_times')
          .select('*')
          .gte('date', today.toISOString().split('T')[0])
          .lte('date', futureDate.toISOString().split('T')[0]);
        setBlockedTimes(blockedData || []);

        // Fetch trade interests
        const { data: interestsData } = await supabase
          .from('trade_interests')
          .select(`
            id, user_id, card_id, is_initiating_card,
            cards (id, name, digital_image_url)
          `)
          .eq('trade_request_id', tradeId);

        const myPicks = interestsData?.filter(i => i.user_id === activeProfile.id) || [];
        const theirPicks = interestsData?.filter(i => i.user_id !== activeProfile.id) || [];
        setMyInterests(myPicks);
        setPartnerInterests(theirPicks);

        // Auto-add ALL trade cards as interests if not already added
        // For requester: these are cards they want from the card owner
        // For card owner: the partner's interest in their cards will show in partner's picks
        if (!isCardOwner && myPicks.length === 0 && allCards.length > 0) {
          // Add all grouped trade cards as initial interests
          const cardsToAdd = allCards.filter(c => c?.id);
          if (cardsToAdd.length > 0) {
            const insertData = cardsToAdd.map((card, idx) => ({
              trade_request_id: primaryTradeId,
              user_id: activeProfile.id,
              card_id: card.id,
              is_initiating_card: idx === 0  // First one is the initiating card
            }));

            await supabase.from('trade_interests').insert(insertData);

            // Refetch to update state
            const { data: refreshedInterests } = await supabase
              .from('trade_interests')
              .select(`
                id, user_id, card_id, is_initiating_card,
                cards (id, name, digital_image_url)
              `)
              .eq('trade_request_id', primaryTradeId);
            setMyInterests(refreshedInterests?.filter(i => i.user_id === activeProfile.id) || []);
          }
        }

      } catch (err) {
        console.error('Error loading chat:', err);
        setError('Failed to load chat');
      } finally {
        setLoading(false);
      }
    };

    if (tradeId && activeProfile) {
      loadData();
    }
  }, [tradeId, activeProfile?.id]);

  // Fetch messages
  const fetchMessages = async () => {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('trade_request_id', tradeId)
      .eq('is_ignored', false)
      .neq('message', '')
      .order('created_at', { ascending: true });

    if (!error && data) {
      setMessages(data);
    }
  };

  // Real-time subscription for messages
  useEffect(() => {
    if (!tradeId) return;

    const channel = supabase
      .channel(`messages-${tradeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `trade_request_id=eq.${tradeId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tradeId]);

  // Real-time subscription for trade interests
  useEffect(() => {
    if (!tradeId || !currentUser) return;

    const channel = supabase
      .channel(`interests-${tradeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trade_interests',
          filter: `trade_request_id=eq.${tradeId}`,
        },
        () => {
          fetchInterests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tradeId, currentUser?.id]);

  // Real-time subscription for schedule changes
  useEffect(() => {
    if (!tradeId) return;

    const channel = supabase
      .channel(`schedule-${tradeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trade_schedules',
          filter: `trade_id=eq.${tradeId}`,
        },
        (payload) => {
          setSchedule(payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tradeId]);

  // Check for location agreement when messages change
  useEffect(() => {
    if (!messages.length || !currentUser || !trade || !schedule) return;
    if (schedule.status !== 'pending_location') return;

    const runLocationAgreementCheck = async () => {
      // Helper to validate location proposal messages
      const isValidLocationProposal = (message) => {
        if (!message.startsWith("I can meet at ")) return false;
        if (!message.endsWith(".")) return false;
        const locationPart = message.substring("I can meet at ".length, message.length - 1);
        if (!locationPart.includes(" in ")) return false;
        return true;
      };

      // Check for existing agreement message
      const hasAgreementMessage = messages.some(msg =>
        msg.sender_id === CHASE_ID &&
        msg.message.includes("Awesome! You've agreed on the location:")
      );
      if (hasAgreementMessage) return;

      // Get location proposals from both users
      const myProposals = messages.filter(msg =>
        msg.sender_id === currentUser.id &&
        !msg.is_ignored &&
        isValidLocationProposal(msg.message)
      );

      const otherUserId = trade.isCardOwner ? trade.partner?.id : trade.card_owner_id;
      const otherProposals = messages.filter(msg =>
        msg.sender_id === otherUserId &&
        !msg.is_ignored &&
        isValidLocationProposal(msg.message)
      );

      if (myProposals.length === 0 || otherProposals.length === 0) return;

      // Extract all location names from proposals
      const extractLocations = (proposals) => {
        const allLocs = [];
        proposals.forEach(msg => {
          const locPart = msg.message.substring("I can meet at ".length, msg.message.length - 1);
          locPart.split(', ').forEach(loc => allLocs.push(loc.trim()));
        });
        return [...new Set(allLocs)];
      };

      const myLocs = extractLocations(myProposals);
      const otherLocs = extractLocations(otherProposals);

      // Find matching locations
      const agreedLocs = myLocs.filter(loc => otherLocs.includes(loc));
      if (agreedLocs.length === 0) return;

      console.log('Location agreement found:', agreedLocs);

      // Only card owner processes the agreement to prevent duplicates
      if (!trade.isCardOwner) return;

      if (agreedLocs.length === 1) {
        // Single match - proceed to scheduling
        const agreedLocName = agreedLocs[0];
        const location = locations.find(l => `${l.store_name} in ${l.city}` === agreedLocName);
        if (!location) return;

        // Update schedule
        await supabase
          .from('trade_schedules')
          .update({
            location_id: location.id,
            status: 'pending_schedule',
          })
          .eq('trade_id', tradeId);

        // Send Chase message about scheduling
        const message = `Awesome! You've agreed on the location: ${agreedLocName}! Now let's find the right day and time to meet up!`;
        await supabase
          .from('messages')
          .insert({
            trade_request_id: tradeId,
            sender_id: CHASE_ID,
            message,
            message_type: 'system',
            is_ignored: false,
          });

        fetchMessages();
      } else {
        // Multiple matches - send simple choice message (no listing)
        const ownerName = currentUser?.username || 'The card owner';
        const message = `You've both agreed on multiple locations! ${ownerName} will pick the final spot.`;

        // Check if choice message already exists
        const hasChoiceMessage = messages.some(msg =>
          msg.sender_id === CHASE_ID &&
          msg.message.includes("agreed on multiple locations")
        );

        if (!hasChoiceMessage) {
          await supabase
            .from('messages')
            .insert({
              trade_request_id: tradeId,
              sender_id: CHASE_ID,
              message,
              message_type: 'system',
              is_ignored: false,
            });
          fetchMessages();
        }

        // Set up choice UI for card owner
        const matchedLocIds = agreedLocs.map(locName => {
          const loc = locations.find(l => `${l.store_name} in ${l.city}` === locName);
          return loc?.id;
        }).filter(Boolean);
        setMatchedLocations(matchedLocIds);
      }
    };

    runLocationAgreementCheck();
  }, [messages, currentUser, trade, schedule, locations, tradeId]);

  // Check for schedule agreement when messages change
  useEffect(() => {
    if (!messages.length || !currentUser || !trade || !schedule) return;
    if (schedule.status !== 'pending_schedule') return;

    const runScheduleAgreementCheck = async () => {
      // Helper to parse schedule message (handles new format)
      const parseScheduleMessage = (message) => {
        const slots = [];
        const lines = message.split('\n');
        let currentDay = null;
        let currentDate = null;

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          // Check for day header like "MON 12/1" or "Mon 12/1"
          const dayMatch = trimmed.match(/^([A-Za-z]{3})\s+(\d{1,2}\/\d{1,2})$/i);
          if (dayMatch) {
            currentDay = dayMatch[1].charAt(0).toUpperCase() + dayMatch[1].slice(1).toLowerCase();
            currentDate = dayMatch[2];
            continue;
          }

          // Also handle old format "Mon 12/1 at times..."
          const oldMatch = trimmed.match(/^([A-Za-z]{3})\s+(\d{1,2}\/\d{1,2})\s+at\s+(.+)$/i);
          if (oldMatch) {
            const dayName = oldMatch[1].charAt(0).toUpperCase() + oldMatch[1].slice(1).toLowerCase();
            const date = oldMatch[2];
            const times = oldMatch[3].split(',').map(t => t.trim()).filter(t => t);
            times.forEach(time => {
              if (time.includes('AM') || time.includes('PM')) {
                slots.push({ date, time, dayName });
              }
            });
            continue;
          }

          // If we have a current day/date, parse times from this line
          if (currentDay && currentDate) {
            const times = trimmed.split(',').map(t => t.trim()).filter(t => t);
            times.forEach(time => {
              if (time.includes('AM') || time.includes('PM')) {
                slots.push({ date: currentDate, time, dayName: currentDay });
              }
            });
          }
        }
        return slots;
      };

      // Check for existing agreement message
      const hasAgreementMessage = messages.some(msg =>
        msg.sender_id === CHASE_ID &&
        msg.message.includes("picked") && msg.message.includes("at") && msg.message.includes("Now please confirm")
      );
      if (hasAgreementMessage) return;

      // Get schedule proposals from both users
      const myProposals = messages.filter(msg =>
        msg.sender_id === currentUser.id &&
        !msg.is_ignored &&
        msg.message.includes("I'm available:")
      );

      const otherUserId = trade.isCardOwner ? trade.partner?.id : trade.card_owner_id;
      const otherProposals = messages.filter(msg =>
        msg.sender_id === otherUserId &&
        !msg.is_ignored &&
        msg.message.includes("I'm available:")
      );

      if (myProposals.length === 0 || otherProposals.length === 0) return;

      // Parse all time slots from both users
      const mySlots = [];
      myProposals.forEach(msg => {
        mySlots.push(...parseScheduleMessage(msg.message));
      });

      const otherSlots = [];
      otherProposals.forEach(msg => {
        otherSlots.push(...parseScheduleMessage(msg.message));
      });

      // Find matching slots
      const matchingSlots = mySlots.filter(mySlot =>
        otherSlots.some(otherSlot =>
          mySlot.date === otherSlot.date && mySlot.time === otherSlot.time
        )
      );

      // Remove duplicates
      const uniqueMatches = matchingSlots.filter((slot, idx, arr) =>
        arr.findIndex(s => s.date === slot.date && s.time === slot.time) === idx
      );

      if (uniqueMatches.length === 0) return;

      console.log('Schedule agreement found:', uniqueMatches);

      // Only card owner processes
      if (!trade.isCardOwner) return;

      if (uniqueMatches.length === 1) {
        // Single match - proceed to confirmation
        const slot = uniqueMatches[0];
        const location = locations.find(l => l.id === schedule.location_id);
        const locName = location ? `${location.store_name} in ${location.city}` : 'the agreed location';
        const ownerName = currentUser?.username || 'The card owner';

        // Update schedule
        await supabase
          .from('trade_schedules')
          .update({
            selected_date: slot.date,
            selected_time: slot.time,
            status: 'pending_confirmation',
          })
          .eq('trade_id', tradeId);

        // Send Chase message
        const message = `${ownerName} picked ${slot.date} at ${slot.time} to meet at ${locName}! Now please confirm below.`;
        await supabase
          .from('messages')
          .insert({
            trade_request_id: tradeId,
            sender_id: CHASE_ID,
            message,
            message_type: 'system',
            is_ignored: false,
          });

        fetchMessages();
      } else {
        // Multiple matches - send simple choice message (no listing)
        const ownerName = currentUser?.username || 'The card owner';
        const hasChoiceMessage = messages.some(msg =>
          msg.sender_id === CHASE_ID &&
          msg.message.includes("Multiple times work")
        );

        if (!hasChoiceMessage) {
          const message = `Multiple times work for both of you! ${ownerName} will pick the final time.`;
          await supabase
            .from('messages')
            .insert({
              trade_request_id: tradeId,
              sender_id: CHASE_ID,
              message,
              message_type: 'system',
              is_ignored: false,
            });
          fetchMessages();
        }

        // Set up choice UI for card owner
        setMatchedTimes(uniqueMatches);
      }
    };

    runScheduleAgreementCheck();
  }, [messages, currentUser, trade, schedule, locations, tradeId]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Get progress step statuses based on schedule
  const getProgressStatus = (step) => {
    const status = schedule?.status || 'pending_location';
    const statusOrder = ['pending_location', 'pending_schedule', 'pending_confirmation', 'confirmed'];
    const currentIndex = statusOrder.indexOf(status);

    if (step === 'location') {
      if (currentIndex > 0) return 'completed';
      if (currentIndex === 0) return 'active';
      return 'pending';
    }
    if (step === 'dateTime') {
      if (currentIndex > 1) return 'completed';
      if (currentIndex === 1) return 'active';
      return 'pending';
    }
    if (step === 'confirm') {
      if (currentIndex === 3) return 'completed';
      if (currentIndex === 2) return 'active';
      return 'pending';
    }
    return 'pending';
  };

  // Toggle location selection
  const toggleLocation = (id) => {
    setSelectedLocations(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  // Submit location selections
  const handleSubmitLocations = async () => {
    if (selectedLocations.length === 0 || submitting) return;
    setSubmitting(true);

    try {
      // Get location names
      const selectedLocs = locations.filter(l => selectedLocations.includes(l.id));
      const locNames = selectedLocs.map(l => `${l.store_name} in ${l.city}`).join(', ');

      // Insert user message
      await supabase
        .from('messages')
        .insert({
          trade_request_id: tradeId,
          sender_id: currentUser.id,
          message: `I can meet at ${locNames}.`,
          message_type: 'user',
          is_ignored: false,
        });

      // Send push notification to partner
      if (trade?.partner?.id) {
        const senderName = currentUser?.username || 'Someone';
        sendNewMessageNotification(trade.partner.id, senderName, `I can meet at ${locNames}`, tradeId);
      }

      // Store location preferences (you might want a separate table for this)
      // For now, we'll track via messages

      setShowLocationModal(false);
      setSelectedLocations([]);
      await fetchMessages();

    } catch (err) {
      console.error('Error submitting locations:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle schedule time selection
  const toggleScheduleTime = (date, time) => {
    setSelectedSchedule(prev => {
      const exists = prev.some(s => s.date === date && s.time === time);
      if (exists) {
        return prev.filter(s => !(s.date === date && s.time === time));
      }
      return [...prev, { date, time }];
    });
  };

  // Select all times for a specific day
  const selectAllTimesForDay = (date) => {
    setSelectedSchedule(prev => {
      // Check if day is fully selected
      const daySlots = prev.filter(s => s.date === date);
      if (daySlots.length === TIME_SLOTS.length) {
        // Deselect all for this day
        return prev.filter(s => s.date !== date);
      }
      // Select all for this day
      const otherDays = prev.filter(s => s.date !== date);
      const allTimesForDay = TIME_SLOTS.map(time => ({ date, time }));
      return [...otherDays, ...allTimesForDay];
    });
  };

  // Select all times for all days
  const selectAllSchedule = () => {
    const days = getNextSevenDays();
    const allSlots = [];
    days.forEach(day => {
      TIME_SLOTS.forEach(time => {
        allSlots.push({ date: day.date, time });
      });
    });
    setSelectedSchedule(allSlots);
  };

  // Clear all selections
  const clearAllSchedule = () => {
    setSelectedSchedule([]);
  };

  // Handle choosing from matched times (card owner picks ONE)
  const handleChooseTime = async () => {
    if (submitting || chosenTimeIdx === null) return;
    setSubmitting(true);

    try {
      const slot = matchedTimes[chosenTimeIdx];
      if (!slot) return;

      const location = locations.find(l => l.id === schedule?.location_id);
      const locName = location ? `${location.store_name} in ${location.city}` : 'the agreed location';
      const ownerName = currentUser?.username || 'The card owner';

      // Update schedule
      await supabase
        .from('trade_schedules')
        .update({
          selected_date: slot.date,
          selected_time: slot.time,
          status: 'pending_confirmation',
        })
        .eq('trade_id', tradeId);

      // Send Chase message
      const message = `${ownerName} picked ${slot.date} at ${slot.time} to meet at ${locName}! Now please confirm below.`;
      await supabase
        .from('messages')
        .insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message,
          message_type: 'system',
          is_ignored: false,
        });

      setShowScheduleChoice(false);
      setMatchedTimes([]);
      setChosenTimeIdx(null);
      fetchMessages();

    } catch (err) {
      console.error('Error choosing time:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle choosing from matched locations (card owner picks ONE)
  const handleChooseLocation = async () => {
    if (submitting || !chosenLocationId) return;
    setSubmitting(true);

    try {
      const location = locations.find(l => l.id === chosenLocationId);
      if (!location) return;

      const locName = `${location.store_name} in ${location.city}`;
      const ownerName = currentUser?.username || 'The card owner';

      // Update schedule with chosen location
      await supabase
        .from('trade_schedules')
        .update({
          location_id: chosenLocationId,
          status: 'pending_schedule',
        })
        .eq('trade_id', tradeId);

      // Send Chase message about the choice - use card owner's name
      const message = `${ownerName} picked ${locName}! Now let's find the right day and time to meet up!`;
      await supabase
        .from('messages')
        .insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message,
          message_type: 'system',
          is_ignored: false,
        });

      setShowLocationChoice(false);
      setMatchedLocations([]);
      setChosenLocationId(null);
      fetchMessages();

    } catch (err) {
      console.error('Error choosing location:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Restart scheduling back to step 1
  const handleRestartScheduling = async () => {
    try {
      // Reset schedule to pending_location
      const { error } = await supabase
        .from('trade_schedules')
        .update({
          location_id: null,
          selected_date: null,
          selected_time: null,
          user1_confirmed: false,
          user2_confirmed: false,
          status: 'pending_location'
        })
        .eq('trade_id', tradeId);

      if (error) throw error;

      // Send system message
      await supabase.from('messages').insert({
        trade_id: tradeId,
        sender_id: CHASE_ID,
        content: `${currentUser?.username || 'User'} has restarted the scheduling process. Please propose new locations.`,
        is_system: true,
      });

      // Close modal and reset states
      setShowRestartConfirm(false);
      setSelectedLocations([]);
      setSelectedSchedule([]);
      setShowLocationChoice(false);
      setShowScheduleChoice(false);
      setMatchedLocations([]);
      setMatchedTimes([]);

    } catch (error) {
      console.error('Error restarting schedule:', error);
      alert('Failed to restart. Please try again.');
    }
  };

  // Fetch trade interests
  const fetchInterests = async () => {
    if (!tradeId || !currentUser) return;

    const { data, error } = await supabase
      .from('trade_interests')
      .select(`
        id, user_id, card_id, is_initiating_card,
        cards (id, name, digital_image_url)
      `)
      .eq('trade_request_id', tradeId);

    if (error) {
      console.error('Error fetching interests:', error);
      return;
    }

    // Filter out interests where the card was soft-deleted (cards join returns null)
    const validData = data?.filter(i => i.cards) || [];
    setMyInterests(validData.filter(i => i.user_id === currentUser?.id));
    setPartnerInterests(validData.filter(i => i.user_id !== currentUser?.id));
  };

  // Fetch partner's tradeable cards for picker
  const fetchPartnerCards = async () => {
    if (!trade?.partner?.id) return;

    const { data, error } = await supabase
      .from('cards')
      .select('id, name, digital_image_url')
      .eq('user_id', trade.partner.id)
      .eq('trading_status', 'TRADE')
      .eq('is_private', false)
      .is('deleted_at', null)
      .order('name');

    if (error) {
      console.error('Error fetching partner cards:', error);
      return;
    }
    setPartnerCards(data || []);
  };

  // Add interest
  const addInterest = async (cardId) => {
    if (!currentUser || !tradeId) return;

    const { error } = await supabase.from('trade_interests').insert({
      trade_request_id: tradeId,
      user_id: currentUser.id,
      card_id: cardId
    });

    if (error) {
      console.error('Error adding interest:', error);
      return;
    }
    fetchInterests();
  };

  // Remove interest
  const removeInterest = async (interestId) => {
    const { error } = await supabase
      .from('trade_interests')
      .delete()
      .eq('id', interestId);

    if (error) {
      console.error('Error removing interest:', error);
      return;
    }
    fetchInterests();
  };

  // Open binder picker
  const openBinderPicker = () => {
    fetchPartnerCards();
    // Pre-select already picked cards
    const alreadyPicked = myInterests.map(i => i.card_id);
    setPendingSelections(alreadyPicked);
    setBinderSearch('');
    setShowBinderPicker(true);
  };

  // Toggle card selection in picker
  const toggleCardSelection = (cardId) => {
    setPendingSelections(prev => {
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      } else {
        return [...prev, cardId];
      }
    });
  };

  // Save binder picker selections
  const saveBinderSelections = async () => {
    // Find cards to add (in pending but not in current)
    const currentIds = myInterests.map(i => i.card_id);
    const toAdd = pendingSelections.filter(id => !currentIds.includes(id));
    const toRemove = myInterests.filter(i => !pendingSelections.includes(i.card_id));

    // Add new selections
    for (const cardId of toAdd) {
      await addInterest(cardId);
    }

    // Remove deselected
    for (const interest of toRemove) {
      await removeInterest(interest.id);
    }

    setShowBinderPicker(false);
  };

  // Submit schedule selections
  const handleSubmitSchedule = async () => {
    if (selectedSchedule.length === 0 || submitting) return;
    setSubmitting(true);

    try {
      // Group times by date for message format
      const timesByDate = {};
      selectedSchedule.forEach(({ date, time }) => {
        if (!timesByDate[date]) {
          // Get day name
          const [month, day] = date.split('/');
          const dateObj = new Date(new Date().getFullYear(), parseInt(month) - 1, parseInt(day));
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          timesByDate[date] = { dayName, times: [] };
        }
        timesByDate[date].times.push(time);
      });

      // Sort times in order and format nicely
      const sortTime = (a, b) => {
        const timeToNum = (t) => {
          const [time, period] = t.split(' ');
          let [hours] = time.split(':').map(Number);
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return hours;
        };
        return timeToNum(a) - timeToNum(b);
      };

      // Format each day cleanly
      const dateStrings = Object.entries(timesByDate).map(([date, { dayName, times }]) => {
        times.sort(sortTime);
        // Split into rows of 4 times for readability
        const timeRows = [];
        for (let i = 0; i < times.length; i += 4) {
          timeRows.push(times.slice(i, i + 4).join(', '));
        }
        return `${dayName.toUpperCase()} ${date}\n${timeRows.join('\n')}`;
      });

      const message = `I'm available:\n\n${dateStrings.join('\n\n')}`;

      // Insert user message
      await supabase
        .from('messages')
        .insert({
          trade_request_id: tradeId,
          sender_id: currentUser.id,
          message,
          message_type: 'user',
          is_ignored: false,
        });

      // Send push notification to partner
      if (trade?.partner?.id) {
        const senderName = currentUser?.username || 'Someone';
        sendNewMessageNotification(trade.partner.id, senderName, "I'm available: (see times)", tradeId);
      }

      setShowScheduleModal(false);
      setSelectedSchedule([]);
      await fetchMessages();

    } catch (err) {
      console.error('Error submitting schedule:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Determine Chase image based on message content
  const getChaseImage = (msg) => {
    if (msg.includes('Hello! I am Chase')) return 'hi';
    if (msg.includes('Great news!') || msg.includes('We did it!') || msg.includes('Awesome!') || msg.includes('picked')) return 'thumbsup';
    return 'think';
  };

  // Check if message needs confirm buttons (show for both users until they confirm)
  const needsConfirmButtons = (msg) => {
    // Never show buttons for expired trades
    if (trade?.isExpired) return false;
    // Show buttons for messages asking for confirmation (original or updated)
    if (!msg.includes("Please confirm") && !msg.includes("please confirm")) return false;
    if (schedule?.status !== 'pending_confirmation') return false;

    // Check if current user has already confirmed
    const isUser1 = trade?.isCardOwner;
    const userConfirmed = isUser1 ? schedule?.user1_confirmed : schedule?.user2_confirmed;

    return !userConfirmed;
  };

  // Handle confirm button
  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const isUser1 = trade?.isCardOwner;
      const updateField = isUser1 ? 'user1_confirmed' : 'user2_confirmed';

      // Update user's confirmation
      await supabase
        .from('trade_schedules')
        .update({ [updateField]: true })
        .eq('trade_id', tradeId);

      // Get fresh schedule data
      const { data: updatedSchedule } = await supabase
        .from('trade_schedules')
        .select('*')
        .eq('trade_id', tradeId)
        .single();

      // Find the existing confirm message (could be original or updated version)
      const confirmMsg = messages.find(m =>
        m.sender_id === CHASE_ID &&
        (m.message.includes('Now please confirm') || m.message.includes('Please confirm below'))
      );

      const location = locations.find(l => l.id === updatedSchedule?.location_id);
      const locName = location ? `${location.store_name} in ${location.city}` : 'your agreed location';
      const ownerName = trade?.isCardOwner
        ? (currentUser?.username || 'Card Owner')
        : (trade?.partner?.username || 'Card Owner');
      const requesterName = trade?.isCardOwner
        ? (trade?.partner?.username || 'Requester')
        : (currentUser?.username || 'Requester');

      if (updatedSchedule?.user1_confirmed && updatedSchedule?.user2_confirmed) {
        // Both confirmed - update status and reset reminder flags
        await supabase
          .from('trade_schedules')
          .update({ status: 'confirmed', reminder_email_sent: false })
          .eq('trade_id', tradeId);

        if (confirmMsg) {
          await supabase
            .from('messages')
            .update({
              message: `We did it! You're all set to meet at ${locName} on ${updatedSchedule.selected_date} at ${updatedSchedule.selected_time}.\n\n${ownerName}: Confirmed\n${requesterName}: Confirmed\n\nGood luck with your trade!`,
            })
            .eq('id', confirmMsg.id);
        }

        // Send push notifications to both users
        const dateTime = `${updatedSchedule.selected_date} at ${updatedSchedule.selected_time}`;
        const cardOwnerId = trade?.isCardOwner ? currentUser?.id : trade?.partner?.id;
        const requesterId = trade?.isCardOwner ? trade?.partner?.id : currentUser?.id;

        // Push to card owner (partner name for them is requester)
        sendTradeScheduledNotification(cardOwnerId, requesterName, locName, dateTime, tradeId);
        // Push to requester (partner name for them is owner)
        sendTradeScheduledNotification(requesterId, ownerName, locName, dateTime, tradeId);

        // Send confirmation emails to both users
        const location = locations.find(l => l.id === updatedSchedule?.location_id);
        sendTradeConfirmationEmails({
          tradeId,
          cardOwnerId,
          requesterId,
          location,
          date: updatedSchedule.selected_date,
          day: '', // Could parse day from date if needed
          time: updatedSchedule.selected_time,
          card: trade?.cardName || 'Card'
        });

        // Notify location staff about the scheduled trade
        if (updatedSchedule?.location_id) {
          sendLocationTradeNotification(
            updatedSchedule.location_id,
            ownerName,
            requesterName,
            dateTime
          );
        }
      } else {
        // Update message to show who confirmed
        const user1Status = updatedSchedule?.user1_confirmed ? 'Confirmed' : 'Waiting...';
        const user2Status = updatedSchedule?.user2_confirmed ? 'Confirmed' : 'Waiting...';

        if (confirmMsg) {
          await supabase
            .from('messages')
            .update({
              message: `Meeting at ${locName} on ${updatedSchedule.selected_date} at ${updatedSchedule.selected_time}. Please confirm below.\n\n${ownerName}: ${user1Status}\n${requesterName}: ${user2Status}`,
            })
            .eq('id', confirmMsg.id);
        }
      }

      fetchMessages();
    } catch (err) {
      console.error('Error confirming:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle decline button
  const handleDecline = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      // Reset the schedule back to pending_schedule
      await supabase
        .from('trade_schedules')
        .update({
          selected_date: null,
          selected_time: null,
          user1_confirmed: false,
          user2_confirmed: false,
          status: 'pending_schedule',
        })
        .eq('trade_id', tradeId);

      const userName = currentUser?.username || 'Someone';
      await supabase
        .from('messages')
        .insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message: `${userName} declined the time. Let's find another time that works for both of you!`,
          message_type: 'system',
          is_ignored: false,
        });

      fetchMessages();
    } catch (err) {
      console.error('Error declining:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Parse trade date/time into a Date object
  const getTradeDateTime = () => {
    if (!schedule?.selected_date || !schedule?.selected_time) return null;
    try {
      const [month, day] = schedule.selected_date.split('/').map(Number);
      const year = new Date().getFullYear();
      const timeMatch = schedule.selected_time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (!timeMatch) return null;
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const ampm = timeMatch[3]?.toUpperCase();
      if (ampm === 'PM' && hours !== 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return new Date(year, month - 1, day, hours, minutes);
    } catch {
      return null;
    }
  };

  // Check if scheduled trade time is in the future
  const isTradeTimeInFuture = () => {
    const dt = getTradeDateTime();
    return dt ? dt > new Date() : false;
  };

  // Check if cancellation would be last-minute (under 24 hours)
  const isLastMinute = () => {
    const dt = getTradeDateTime();
    if (!dt) return false;
    const hoursUntil = (dt.getTime() - Date.now()) / (1000 * 60 * 60);
    return hoursUntil < 24;
  };

  // Calculate reschedule response expiry (1 hour before trade time)
  const getRescheduleExpiry = () => {
    const dt = getTradeDateTime();
    if (!dt) return new Date(Date.now() + 24 * 60 * 60 * 1000); // fallback: 24hrs from now
    return new Date(dt.getTime() - 60 * 60 * 1000); // 1 hour before trade
  };

  // Handle cancel confirmed trade (with consequences)
  const handleCancelTrade = async () => {
    if (submitting) return;
    setSubmitting(true);
    setShowCancelConfirm(false);
    setShowActionMenu(false);

    try {
      const userName = currentUser?.username || 'Someone';
      const partnerId = trade?.partner?.id;
      const lastMinute = isLastMinute();

      // Cancel all trade_schedules for this trade
      await supabase
        .from('trade_schedules')
        .update({
          status: 'cancelled',
          cancelled_by: currentUser?.id,
          cancelled_at: new Date().toISOString(),
          cancel_type: 'cancel',
          is_last_minute: lastMinute,
        })
        .eq('trade_id', tradeId);

      // Cancel all trade_requests in the group
      const tradeIds = trade?.groupedTradeIds || [tradeId];
      for (const tid of tradeIds) {
        await supabase
          .from('trade_requests')
          .update({ status: 'cancelled' })
          .eq('id', tid);
      }

      // Increment user cancel stats
      const { data: userData } = await supabase.from('users').select('trades_cancelled, trades_cancelled_last_minute').eq('id', currentUser?.id).single();
      const updates = { trades_cancelled: (userData?.trades_cancelled || 0) + 1 };
      if (lastMinute) updates.trades_cancelled_last_minute = (userData?.trades_cancelled_last_minute || 0) + 1;
      await supabase.from('users').update(updates).eq('id', currentUser?.id);

      // Build system message
      const lastMinuteWarning = lastMinute
        ? `\n\nThis was a last-minute cancellation and has been noted on @${userName}'s trading profile.`
        : '';
      await supabase.from('messages').insert({
        trade_request_id: tradeId,
        sender_id: CHASE_ID,
        message: `@${userName} has cancelled this trade.${lastMinuteWarning}`,
        message_type: 'system',
        is_ignored: false,
      });

      // Push notification to partner
      if (partnerId) {
        const location = locations.find(l => l.id === schedule?.location_id);
        sendTradeCancelledNotification(partnerId, userName, location?.store_name || 'the trade location', tradeId);
      }

      // Send cancellation emails
      sendCancellationEmails({
        cancelledByUserId: currentUser?.id,
        otherUserId: partnerId,
        tradeId,
        locationId: schedule?.location_id,
        date: schedule?.selected_date,
        time: schedule?.selected_time,
        card: trade?.cardName || 'Card',
        isLastMinute: lastMinute,
      });

      fetchMessages();
    } catch (err) {
      console.error('Error cancelling trade:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle "Running X min late"
  const handleRunningLate = async (minutes) => {
    if (submitting) return;
    setSubmitting(true);
    setShowLateOptions(false);
    setShowActionMenu(false);

    try {
      const userName = currentUser?.username || 'Someone';
      const partnerId = trade?.partner?.id;

      // Save late notice on schedule
      await supabase
        .from('trade_schedules')
        .update({
          late_minutes: minutes,
          reschedule_requested_by: currentUser?.id,
          reschedule_response: null,
          reschedule_expires_at: getRescheduleExpiry().toISOString(),
        })
        .eq('trade_id', tradeId);

      // Insert system message (Chase asks Party B)
      await supabase.from('messages').insert({
        trade_request_id: tradeId,
        sender_id: CHASE_ID,
        message: `@${userName} is running ${minutes} minutes late. Is that OK?`,
        message_type: 'late_notice',
        is_ignored: false,
      });

      // Push to partner
      if (partnerId) {
        sendRunningLateNotification(partnerId, userName, minutes, tradeId);
      }

      fetchMessages();
    } catch (err) {
      console.error('Error sending late notice:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reschedule request
  const handleReschedule = async () => {
    if (submitting || !rescheduleDate || !rescheduleTime) return;
    setSubmitting(true);
    setShowRescheduleModal(false);
    setShowActionMenu(false);

    try {
      const userName = currentUser?.username || 'Someone';
      const partnerId = trade?.partner?.id;

      // Save reschedule request on schedule
      await supabase
        .from('trade_schedules')
        .update({
          reschedule_requested_by: currentUser?.id,
          reschedule_new_date: rescheduleDate,
          reschedule_new_time: rescheduleTime,
          reschedule_response: null,
          reschedule_expires_at: getRescheduleExpiry().toISOString(),
          late_minutes: null,
        })
        .eq('trade_id', tradeId);

      // Insert system message
      await supabase.from('messages').insert({
        trade_request_id: tradeId,
        sender_id: CHASE_ID,
        message: `@${userName} wants to reschedule to ${rescheduleDate} at ${rescheduleTime}. Does that work?`,
        message_type: 'reschedule_request',
        is_ignored: false,
      });

      // Push + email to partner
      if (partnerId) {
        sendRescheduleRequestNotification(partnerId, userName, `${rescheduleDate} at ${rescheduleTime}`, tradeId);
      }

      // Send reschedule emails
      const location = locations.find(l => l.id === schedule?.location_id);
      sendRescheduleEmails({
        requestedByUserId: currentUser?.id,
        otherUserId: partnerId,
        locationId: schedule?.location_id,
        originalDate: schedule?.selected_date,
        originalTime: schedule?.selected_time,
        newDate: rescheduleDate,
        newTime: rescheduleTime,
        card: trade?.cardName || 'Card',
      });

      setRescheduleDate('');
      setRescheduleTime('');
      fetchMessages();
    } catch (err) {
      console.error('Error requesting reschedule:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Party B responding to late notice
  const handleLateResponse = async (accepted) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const userName = currentUser?.username || 'Someone';
      const partnerName = trade?.partnerName || 'your trading partner';
      const partnerId = trade?.partner?.id;
      const requesterId = schedule?.reschedule_requested_by;

      if (accepted) {
        // Accepted: update schedule, clear late notice
        await supabase.from('trade_schedules').update({
          reschedule_response: 'accepted',
          reschedule_expires_at: null,
        }).eq('trade_id', tradeId);

        await supabase.from('messages').insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message: `@${userName} is OK with the delay. Trade is still on!`,
          message_type: 'system',
          is_ignored: false,
        });

        if (requesterId) sendLateResponseNotification(requesterId, userName, true, tradeId);
      } else {
        // Declined: cancel the trade
        const lastMinute = isLastMinute();
        await supabase.from('trade_schedules').update({
          status: 'cancelled',
          reschedule_response: 'declined',
          cancelled_by: requesterId,
          cancelled_at: new Date().toISOString(),
          cancel_type: 'late_declined',
          is_last_minute: lastMinute,
        }).eq('trade_id', tradeId);

        const tradeIds = trade?.groupedTradeIds || [tradeId];
        for (const tid of tradeIds) {
          await supabase.from('trade_requests').update({ status: 'cancelled' }).eq('id', tid);
        }

        // Increment canceller stats (the person who was late = Party A)
        if (requesterId && lastMinute) {
          const { data: ud } = await supabase.from('users').select('trades_cancelled, trades_cancelled_last_minute').eq('id', requesterId).single();
          await supabase.from('users').update({
            trades_cancelled: (ud?.trades_cancelled || 0) + 1,
            trades_cancelled_last_minute: (ud?.trades_cancelled_last_minute || 0) + 1,
          }).eq('id', requesterId);
        }

        await supabase.from('messages').insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message: `@${userName} declined the delay. Trade has been cancelled.${lastMinute ? `\n\nThis last-minute cancellation has been noted on @${partnerName}'s profile.` : ''}`,
          message_type: 'system',
          is_ignored: false,
        });

        if (requesterId) sendLateResponseNotification(requesterId, userName, false, tradeId);
      }

      fetchMessages();
    } catch (err) {
      console.error('Error handling late response:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Party B picking a specific time from reschedule options
  const handleRescheduleResponseWithTime = async (date, time) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const userName = currentUser?.username || 'Someone';
      const requesterId = schedule?.reschedule_requested_by;

      await supabase.from('trade_schedules').update({
        selected_date: date,
        selected_time: time,
        reschedule_response: 'accepted',
        reschedule_expires_at: null,
        reschedule_new_date: null,
        reschedule_new_time: null,
        reschedule_requested_by: null,
        reminder_1day_sent: false,
        reminder_dayof_sent: false,
        reminder_15min_sent: false,
        reminder_email_sent: false,
      }).eq('trade_id', tradeId);

      await supabase.from('messages').insert({
        trade_request_id: tradeId,
        sender_id: CHASE_ID,
        message: `@${userName} picked ${date} at ${time}. Trade is rescheduled!`,
        message_type: 'system',
        is_ignored: false,
      });

      if (requesterId) sendRescheduleResponseNotification(requesterId, userName, true, tradeId);
      fetchMessages();
    } catch (err) {
      console.error('Error handling reschedule time pick:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Party B responding to reschedule request
  const handleRescheduleResponse = async (accepted) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const userName = currentUser?.username || 'Someone';
      const partnerName = trade?.partnerName || 'your trading partner';
      const partnerId = trade?.partner?.id;
      const requesterId = schedule?.reschedule_requested_by;

      if (accepted) {
        // Accepted: update schedule to new date/time
        await supabase.from('trade_schedules').update({
          selected_date: schedule?.reschedule_new_date,
          selected_time: schedule?.reschedule_new_time,
          reschedule_response: 'accepted',
          reschedule_expires_at: null,
          reschedule_new_date: null,
          reschedule_new_time: null,
          reschedule_requested_by: null,
          reminder_1day_sent: false,
          reminder_dayof_sent: false,
          reminder_15min_sent: false,
          reminder_email_sent: false,
        }).eq('trade_id', tradeId);

        await supabase.from('messages').insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message: `@${userName} accepted the reschedule! Trade is now set for ${schedule?.reschedule_new_date} at ${schedule?.reschedule_new_time}. Good luck!`,
          message_type: 'system',
          is_ignored: false,
        });

        if (requesterId) sendRescheduleResponseNotification(requesterId, userName, true, tradeId);
      } else {
        // Declined: cancel the trade
        const lastMinute = isLastMinute();
        await supabase.from('trade_schedules').update({
          status: 'cancelled',
          reschedule_response: 'declined',
          cancelled_by: requesterId,
          cancelled_at: new Date().toISOString(),
          cancel_type: 'reschedule_declined',
          is_last_minute: lastMinute,
        }).eq('trade_id', tradeId);

        const tradeIds = trade?.groupedTradeIds || [tradeId];
        for (const tid of tradeIds) {
          await supabase.from('trade_requests').update({ status: 'cancelled' }).eq('id', tid);
        }

        // Increment canceller stats (Party A = the one who wanted to reschedule)
        if (requesterId) {
          const { data: ud } = await supabase.from('users').select('trades_cancelled, trades_cancelled_last_minute').eq('id', requesterId).single();
          const updates = { trades_cancelled: (ud?.trades_cancelled || 0) + 1 };
          if (lastMinute) updates.trades_cancelled_last_minute = (ud?.trades_cancelled_last_minute || 0) + 1;
          await supabase.from('users').update(updates).eq('id', requesterId);
        }

        await supabase.from('messages').insert({
          trade_request_id: tradeId,
          sender_id: CHASE_ID,
          message: `@${userName} declined the reschedule. Trade has been cancelled.${lastMinute ? `\n\nThis last-minute cancellation has been noted on @${partnerName}'s profile.` : ''}`,
          message_type: 'system',
          is_ignored: false,
        });

        if (requesterId) sendRescheduleResponseNotification(requesterId, userName, false, tradeId);

        sendCancellationEmails({
          cancelledByUserId: requesterId,
          otherUserId: currentUser?.id,
          tradeId,
          locationId: schedule?.location_id,
          date: schedule?.selected_date,
          time: schedule?.selected_time,
          card: trade?.cardName || 'Card',
          isLastMinute: lastMinute,
        });
      }

      fetchMessages();
    } catch (err) {
      console.error('Error handling reschedule response:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if current user needs to respond to a late/reschedule request
  const needsLateResponse = () => {
    return schedule?.late_minutes && !schedule?.reschedule_response && schedule?.reschedule_requested_by !== currentUser?.id;
  };

  const needsRescheduleResponse = () => {
    return schedule?.reschedule_new_date && !schedule?.reschedule_response && schedule?.reschedule_requested_by !== currentUser?.id;
  };

  // Render loading
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  // Render error
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <ArrowLeftIcon />
          </button>
          <div style={styles.headerContent}>
            <h1 style={styles.headerTitle}>Error</h1>
            <span style={styles.headerSubtitle}>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  const scheduleStatus = schedule?.status || 'pending_location';

  // Check for choice messages from Chase (to show waiting state for non-card-owner)
  const hasLocationChoiceMessage = messages.some(msg =>
    msg.sender_id === CHASE_ID &&
    msg.message.includes("agreed on multiple locations")
  );

  const hasScheduleChoiceMessage = messages.some(msg =>
    msg.sender_id === CHASE_ID &&
    msg.message.includes("Multiple times work")
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>Trade Chat</h1>
          <span style={styles.headerSubtitle}>with {trade?.partnerName}</span>
        </div>
        <button
          style={styles.detailsButton}
          onClick={() => {
            fetchInterests();
            setShowDetailsModal(true);
          }}
        >
          Details
        </button>
      </div>

      {/* Status Banner */}
      {trade?.isExpired ? (
        // Expired Banner
        <div style={styles.expiredBanner}>
          <div style={styles.expiredIndicator}>
            <CloseIcon />
          </div>
          <div style={styles.expiredContent}>
            <span style={styles.expiredTitle}>Conversation Expired</span>
            <span style={styles.expiredSubtext}>This trade was not scheduled within 3 days</span>
          </div>
          <button
            style={styles.viewCollectionBtn}
            onClick={() => navigate(`/profile/${trade?.partner?.id}`)}
          >
            View Collection
          </button>
        </div>
      ) : scheduleStatus === 'cancelled' || trade?.status === 'cancelled' ? (
        <div style={styles.expiredBanner}>
          <div style={styles.expiredIndicator}>
            <CloseIcon />
          </div>
          <div style={styles.expiredContent}>
            <span style={styles.expiredTitle}>Trade Cancelled</span>
            <span style={styles.expiredSubtext}>This trade has been cancelled</span>
          </div>
        </div>
      ) : scheduleStatus === 'confirmed' ? (
        <button style={styles.statusBannerBtn} onClick={() => setShowTradeSummary(true)}>
          <div style={{...styles.statusIndicator, ...styles.statusConfirmed}}>
            <span style={styles.statusStep}>3/3</span>
          </div>
          <span style={styles.statusText}>Trade Scheduled</span>
          <span style={styles.statusTapHint}>Tap for Details</span>
        </button>
      ) : (
        <div style={styles.statusBanner}>
          <div style={{
            ...styles.statusIndicator,
            ...(scheduleStatus === 'pending_confirmation' ? styles.statusPendingConfirm :
                scheduleStatus === 'pending_schedule' ? styles.statusPendingSchedule :
                styles.statusPendingLocation),
          }}>
            <span style={styles.statusStep}>
              {scheduleStatus === 'pending_confirmation' ? '3/3' :
               scheduleStatus === 'pending_schedule' ? '2/3' : '1/3'}
            </span>
          </div>
          <span style={styles.statusText}>
            {scheduleStatus === 'pending_confirmation' ? 'Awaiting Confirmation' :
             scheduleStatus === 'pending_schedule' ? 'Picking a Time' : 'Choosing Location'}
          </span>
          {/* Restart button - only show when past step 1 */}
          {(scheduleStatus === 'pending_schedule' || scheduleStatus === 'pending_confirmation') && (
            <button
              style={styles.restartBtn}
              onClick={() => setShowRestartConfirm(true)}
            >
              Restart
            </button>
          )}
          <div style={styles.statusProgress}>
            <div style={{
              ...styles.statusProgressFill,
              width: scheduleStatus === 'pending_confirmation' ? '100%' :
                     scheduleStatus === 'pending_schedule' ? '66%' : '33%',
            }} />
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div style={styles.messagesArea}>
        {messages.map(msg => {
          const isChase = msg.sender_id === CHASE_ID;
          const isMe = msg.sender_id === currentUser?.id;
          const isRestartMessage = msg.message?.includes('restarted the scheduling');

          // Special restart divider
          if (isRestartMessage) {
            return (
              <div key={msg.id} style={styles.restartDivider}>
                <div style={styles.restartDividerLine} />
                <span style={styles.restartDividerText}>Scheduling Restarted</span>
                <div style={styles.restartDividerLine} />
              </div>
            );
          }

          if (isChase) {
            // Late notice - Party B sees OK/No buttons
            if (msg.message_type === 'late_notice' && needsLateResponse()) {
              return (
                <ChaseMessage
                  key={msg.id}
                  message={msg.message}
                  chaseImage="think"
                  showConfirmButtons={true}
                  onConfirm={() => handleLateResponse(true)}
                  onDecline={() => handleLateResponse(false)}
                  confirmLabel="OK"
                  declineLabel="No, Cancel"
                />
              );
            }

            // Reschedule request - Party B sees time choice buttons or Accept/Decline
            if (msg.message_type === 'reschedule_request' && needsRescheduleResponse()) {
              // Parse time options from numbered list format: "1. 3/21 at 3:00 PM"
              const timeOptions = [];
              const lines = (msg.message || '').split('\n');
              lines.forEach(line => {
                const match = line.match(/^\d+\.\s*(\d+\/\d+)\s+at\s+(.+)$/);
                if (match) timeOptions.push({ date: match[1], time: match[2].trim() });
              });

              if (timeOptions.length > 1) {
                // Multiple options - show each as a button + cancel
                return (
                  <div key={msg.id} style={styles.chaseMessageContainer}>
                    <img src={ChaseThink} alt="Chase" style={styles.chaseAvatar} />
                    <div style={styles.chaseMessageBubble}>
                      <span style={styles.chaseName}>Chase</span>
                      <p style={styles.chaseMessageText}>
                        @{trade?.partnerName} wants to reschedule. Pick a time:
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                        {timeOptions.map((opt, i) => (
                          <button
                            key={i}
                            style={styles.confirmYesButton}
                            onClick={() => handleRescheduleResponseWithTime(opt.date, opt.time)}
                          >
                            {opt.date} at {opt.time}
                          </button>
                        ))}
                        <button style={styles.confirmNoButton} onClick={() => handleRescheduleResponse(false)}>
                          No, Cancel Trade
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              // Single option - simple Accept/Decline
              return (
                <ChaseMessage
                  key={msg.id}
                  message={msg.message}
                  chaseImage="think"
                  showConfirmButtons={true}
                  onConfirm={() => handleRescheduleResponse(true)}
                  onDecline={() => handleRescheduleResponse(false)}
                  confirmLabel="Accept"
                  declineLabel="Decline"
                />
              );
            }

            return (
              <ChaseMessage
                key={msg.id}
                message={msg.message}
                chaseImage={getChaseImage(msg.message)}
                showConfirmButtons={needsConfirmButtons(msg.message)}
                onConfirm={handleConfirm}
                onDecline={handleDecline}
              />
            );
          }

          return (
            <UserMessage
              key={msg.id}
              isMe={isMe}
              message={msg.message}
              partnerName={trade?.partnerName}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Trade Actions - show when confirmed, future, not cancelled */}
      {!trade?.isExpired && scheduleStatus === 'confirmed' && isTradeTimeInFuture() && trade?.status !== 'cancelled' && (
        <div style={styles.bottomActions}>
          {schedule?.reschedule_response ? (
            /* Response received - only cancel remains */
            <button style={styles.actionBtnCancel} onClick={() => setShowCancelConfirm(true)}>
              <CloseIcon size={16} />
              <span>Cancel Trade</span>
            </button>
          ) : (schedule?.late_minutes || schedule?.reschedule_new_date) ? (
            /* Already sent a late/reschedule request - only cancel allowed */
            <>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' }}>
                <span style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                  Waiting for response. You can still cancel.
                </span>
              </div>
              <button style={styles.actionBtnCancel} onClick={() => setShowCancelConfirm(true)}>
                <CloseIcon size={16} />
                <span>Cancel</span>
              </button>
            </>
          ) : pendingLateMinutes ? (
            /* Confirm late notice */
            <>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '0 8px' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', textAlign: 'center' }}>
                  Notify {pendingLateMinutes} min late?
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
                  You can only cancel after this. No further changes.
                </span>
              </div>
              <button style={styles.actionBtnWarn} onClick={() => { handleRunningLate(pendingLateMinutes); setPendingLateMinutes(null); }}>
                <CheckIcon size={16} />
                <span>Send</span>
              </button>
              <button style={{ ...styles.actionBtn, flex: 'none', padding: '14px' }} onClick={() => setPendingLateMinutes(null)}>
                <CloseIcon size={16} />
              </button>
            </>
          ) : showLateOptions ? (
            <>
              <button style={styles.actionBtnWarn} onClick={() => setPendingLateMinutes(15)}>
                <ClockIcon />
                <span>15 min</span>
              </button>
              <button style={styles.actionBtnWarn} onClick={() => setPendingLateMinutes(30)}>
                <ClockIcon />
                <span>30 min</span>
              </button>
              <button style={{ ...styles.actionBtn, flex: 'none', padding: '14px' }} onClick={() => setShowLateOptions(false)}>
                <CloseIcon size={16} />
              </button>
            </>
          ) : (
            <>
              <button style={styles.actionBtnWarn} onClick={() => setShowLateOptions(true)}>
                <ClockIcon />
                <span>Running Late</span>
              </button>
              <button style={styles.actionBtn} onClick={() => setShowRescheduleModal(true)}>
                <CalendarIcon />
                <span>Reschedule</span>
              </button>
              <button style={styles.actionBtnCancel} onClick={() => setShowCancelConfirm(true)}>
                <CloseIcon size={16} />
                <span>Cancel</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Bottom Actions - hide when expired or confirmed or cancelled */}
      {scheduleStatus !== 'confirmed' && scheduleStatus !== 'cancelled' && !trade?.isExpired && trade?.status !== 'cancelled' && (
        <div style={styles.bottomActions}>
          {scheduleStatus === 'pending_location' && hasLocationChoiceMessage && trade?.isCardOwner && (
            <button style={styles.actionBtnGreen} onClick={() => setShowLocationChoice(true)}>
              <CheckIcon size={16} />
              <span>Choose Location ({matchedLocations.length})</span>
            </button>
          )}
          {scheduleStatus === 'pending_location' && hasLocationChoiceMessage && !trade?.isCardOwner && (
            <button style={styles.actionBtnDisabled} disabled>
              <ClockIcon />
              <span>Waiting for selection</span>
            </button>
          )}
          {scheduleStatus === 'pending_location' && !hasLocationChoiceMessage && (
            <button style={styles.actionBtn} onClick={() => setShowLocationModal(true)}>
              <MapPinIcon />
              <span>Select Location</span>
            </button>
          )}
          {scheduleStatus === 'pending_schedule' && hasScheduleChoiceMessage && trade?.isCardOwner && (
            <button style={styles.actionBtnGreen} onClick={() => setShowScheduleChoice(true)}>
              <CheckIcon size={16} />
              <span>Choose Time ({matchedTimes.length})</span>
            </button>
          )}
          {scheduleStatus === 'pending_schedule' && hasScheduleChoiceMessage && !trade?.isCardOwner && (
            <button style={styles.actionBtnDisabled} disabled>
              <ClockIcon />
              <span>Waiting for selection</span>
            </button>
          )}
          {scheduleStatus === 'pending_schedule' && !hasScheduleChoiceMessage && (
            <button style={styles.actionBtn} onClick={() => setShowScheduleModal(true)}>
              <CalendarIcon />
              <span>Send Schedule</span>
            </button>
          )}
          <button style={styles.actionBtnSecondary}>
            <ClockIcon />
            <span>Wait</span>
          </button>
        </div>
      )}

      {/* Location Modal */}
      <LocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        locations={locations}
        selectedIds={selectedLocations}
        onToggle={toggleLocation}
        onSubmit={handleSubmitLocations}
        loading={submitting}
        currentUser={currentUser}
        partner={trade?.partner}
        blockedTimes={blockedTimes}
      />

      {/* Location Choice Modal - for picking from matched locations */}
      {showLocationChoice && (
        <div style={styles.modalOverlay} onClick={() => { setShowLocationChoice(false); setChosenLocationId(null); }}>
          <div style={styles.modalContentCenter} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeaderRow}>
              <button style={styles.modalCloseBtn} onClick={() => { setShowLocationChoice(false); setChosenLocationId(null); }}>
                <CloseIcon />
              </button>
              <div style={styles.modalHeaderCenter}>
                <h2 style={styles.modalTitle}>Choose Location</h2>
                <span style={styles.selectedCountSmall}>Pick your preferred spot</span>
              </div>
              <button
                style={{
                  ...styles.submitBtnSmall,
                  ...(!chosenLocationId || submitting ? styles.submitBtnSmallDisabled : {}),
                }}
                onClick={handleChooseLocation}
                disabled={!chosenLocationId || submitting}
              >
                {submitting ? '...' : 'Done'}
              </button>
            </div>
            <div style={styles.choiceList}>
              {matchedLocations.map(locId => {
                const loc = locations.find(l => l.id === locId);
                if (!loc) return null;
                const isSelected = chosenLocationId === locId;
                return (
                  <button
                    key={locId}
                    style={{
                      ...styles.choiceItem,
                      ...(isSelected ? styles.choiceItemSelected : {}),
                    }}
                    onClick={() => setChosenLocationId(locId)}
                  >
                    <div style={styles.choiceItemInfo}>
                      <span style={styles.choiceItemName}>{loc.store_name}</span>
                      <span style={styles.choiceItemCity}>{loc.city}, {loc.state}</span>
                    </div>
                    <div style={{
                      ...styles.radioCircle,
                      ...(isSelected ? styles.radioCircleSelected : {}),
                    }}>
                      {isSelected && <div style={styles.radioInner} />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Choice Modal - nice day tabs + time grid for picking ONE time */}
      {showScheduleChoice && (() => {
        // Group matched times by date
        const timesByDate = {};
        matchedTimes.forEach((slot, idx) => {
          if (!timesByDate[slot.date]) {
            timesByDate[slot.date] = { dayName: slot.dayName, times: [] };
          }
          timesByDate[slot.date].times.push({ time: slot.time, idx });
        });
        const availableDates = Object.keys(timesByDate);
        const selectedSlot = chosenTimeIdx !== null ? matchedTimes[chosenTimeIdx] : null;
        const activeDate = choiceActiveDate || availableDates[0];

        return (
          <div style={styles.modalOverlay} onClick={() => { setShowScheduleChoice(false); setChosenTimeIdx(null); setChoiceActiveDate(null); }}>
            <div style={styles.modalContentCenter} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeaderRow}>
                <button style={styles.modalCloseBtn} onClick={() => { setShowScheduleChoice(false); setChosenTimeIdx(null); setChoiceActiveDate(null); }}>
                  <CloseIcon />
                </button>
                <div style={styles.modalHeaderCenter}>
                  <h2 style={styles.modalTitle}>Choose Meeting Time</h2>
                  <span style={styles.selectedCountSmall}>
                    {selectedSlot ? `${selectedSlot.dayName} ${selectedSlot.date} at ${selectedSlot.time}` : 'Pick ONE time'}
                  </span>
                </div>
                <button
                  style={{
                    ...styles.submitBtnSmall,
                    ...(chosenTimeIdx === null || submitting ? styles.submitBtnSmallDisabled : {}),
                  }}
                  onClick={handleChooseTime}
                  disabled={chosenTimeIdx === null || submitting}
                >
                  {submitting ? '...' : 'Done'}
                </button>
              </div>

              {/* Day Tabs - only show days with matched times */}
              <div style={styles.dayTabs}>
                {availableDates.map(date => {
                  const { dayName } = timesByDate[date];
                  const isActive = activeDate === date;
                  const hasSelection = selectedSlot?.date === date;
                  return (
                    <button
                      key={date}
                      style={{
                        ...styles.dayTab,
                        ...(isActive ? styles.dayTabActive : {}),
                      }}
                      onClick={() => setChoiceActiveDate(date)}
                    >
                      <span style={styles.dayTabDay}>{dayName}</span>
                      <span style={styles.dayTabDate}>{date}</span>
                      {hasSelection && (
                        <div style={styles.dayTabBadge}>
                          <CheckIcon size={10} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Time Grid - only show matched times for active date */}
              <div style={styles.timeSlotGrid}>
                {timesByDate[activeDate]?.times.map(({ time, idx }) => {
                  const isSelected = chosenTimeIdx === idx;
                  return (
                    <button
                      key={idx}
                      style={{
                        ...styles.timeSlotBtn,
                        ...(isSelected ? styles.timeSlotBtnSelected : {}),
                      }}
                      onClick={() => setChosenTimeIdx(idx)}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        selectedSlots={selectedSchedule}
        onToggle={toggleScheduleTime}
        onSelectAllDay={selectAllTimesForDay}
        onSelectAll={selectAllSchedule}
        onClearAll={clearAllSchedule}
        onSubmit={handleSubmitSchedule}
        loading={submitting}
        operatingHours={locations.find(l => l.id === schedule?.location_id)?.operating_hours}
        blockedTimes={blockedTimes}
        locationId={schedule?.location_id}
      />

      {/* Trade Summary Modal */}
      <TradeSummaryModal
        isOpen={showTradeSummary}
        onClose={() => setShowTradeSummary(false)}
        trade={trade}
        schedule={schedule}
        locations={locations}
      />

      {/* Restart Confirmation Modal */}
      {showRestartConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowRestartConfirm(false)}>
          <div style={styles.confirmModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Restart Scheduling?</h3>
            <p style={styles.confirmText}>
              This will clear the current location and time selections.
              You'll need to start over with proposing locations.
            </p>
            <p style={styles.confirmSubtext}>
              Your chat history will be preserved.
            </p>
            <div style={styles.confirmButtons}>
              <button
                style={styles.confirmCancelBtn}
                onClick={() => setShowRestartConfirm(false)}
              >
                Cancel
              </button>
              <button
                style={styles.confirmRestartBtn}
                onClick={handleRestartScheduling}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Trade Confirmation Modal */}
      {showCancelConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowCancelConfirm(false)}>
          <div style={styles.confirmModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Cancel Trade?</h3>
            <p style={styles.confirmText}>
              This will cancel the scheduled trade with {trade?.partnerName}.
              Everyone will be notified (trading partner, parents, and the location).
            </p>
            {isLastMinute() && (
              <p style={{ ...styles.confirmText, color: 'var(--error, #ef4444)', fontWeight: 600, marginTop: 8 }}>
                This is a last-minute cancellation (under 24 hours) and will be recorded on your trading profile.
              </p>
            )}
            <div style={styles.confirmButtons}>
              <button
                style={styles.confirmCancelBtn}
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Trade
              </button>
              <button
                style={styles.confirmRestartBtn}
                onClick={handleCancelTrade}
              >
                Cancel Trade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <>
          {/* Warning banner above the schedule modal */}
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001,
            padding: '12px 16px', textAlign: 'center',
            backgroundColor: 'rgba(245, 158, 11, 0.95)', color: '#fff',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Pick up to 3 times. Once sent, you can only cancel.
            </span>
            {isLastMinute() && (
              <span style={{ fontSize: 11, display: 'block', marginTop: 2, opacity: 0.9 }}>
                If declined, this counts as a last-minute cancellation.
              </span>
            )}
          </div>
          <ScheduleModal
            isOpen={true}
            onClose={() => { setShowRescheduleModal(false); setRescheduleSlots([]); }}
            selectedSlots={rescheduleSlots}
            onToggle={(date, time) => {
              setRescheduleSlots(prev => {
                const exists = prev.some(s => s.date === date && s.time === time);
                if (exists) return prev.filter(s => !(s.date === date && s.time === time));
                if (prev.length >= 3) return prev; // Max 3 slots
                return [...prev, { date, time }];
              });
            }}
            onSelectAllDay={() => {}}
            onSelectAll={() => {}}
            onClearAll={() => setRescheduleSlots([])}
            hideSelectAll={true}
            onSubmit={async () => {
              if (rescheduleSlots.length === 0) return;
              setShowRescheduleModal(false);
              if (submitting) return;
              setSubmitting(true);
              try {
                const userName = currentUser?.username || 'Someone';
                const partnerId = trade?.partner?.id;
                // Format the options for the message
                const optionsText = rescheduleSlots.map(s => `${s.date} at ${s.time}`).join(', ');
                // Store first option as the "new" date/time on the schedule
                const firstSlot = rescheduleSlots[0];
                await supabase.from('trade_schedules').update({
                  reschedule_requested_by: currentUser?.id,
                  reschedule_new_date: firstSlot.date,
                  reschedule_new_time: firstSlot.time,
                  reschedule_response: null,
                  reschedule_expires_at: getRescheduleExpiry().toISOString(),
                  late_minutes: null,
                }).eq('trade_id', tradeId);
                const msg = rescheduleSlots.length === 1
                  ? `@${userName} wants to reschedule to ${optionsText}. Does that work?`
                  : `@${userName} wants to reschedule. Pick a time:\n${rescheduleSlots.map((s, i) => `${i + 1}. ${s.date} at ${s.time}`).join('\n')}\n\nOr cancel the trade.`;
                await supabase.from('messages').insert({
                  trade_request_id: tradeId,
                  sender_id: CHASE_ID,
                  message: msg,
                  message_type: 'reschedule_request',
                  is_ignored: false,
                });
                if (partnerId) {
                  sendRescheduleRequestNotification(partnerId, userName, optionsText, tradeId);
                }
                sendRescheduleEmails({
                  requestedByUserId: currentUser?.id,
                  otherUserId: partnerId,
                  locationId: schedule?.location_id,
                  originalDate: schedule?.selected_date,
                  originalTime: schedule?.selected_time,
                  newDate: firstSlot.date,
                  newTime: firstSlot.time,
                  card: trade?.cardName || 'Card',
                });
                setRescheduleSlots([]);
                fetchMessages();
              } catch (err) {
                console.error('Error requesting reschedule:', err);
              } finally {
                setSubmitting(false);
              }
            }}
            loading={submitting}
            operatingHours={locations.find(l => l.id === schedule?.location_id)?.operating_hours}
            blockedTimes={blockedTimes}
            locationId={schedule?.location_id}
          />
        </>
      )}

      {/* Trade Details Modal */}
      {showDetailsModal && (() => {
        // For card owner: trade.cards are what the requester wants (partner's picks)
        // For requester: myInterests already includes trade.cards from auto-add
        // Build partner picks display:
        // - If card owner: show trade.cards (what partner wants from you)
        // - If requester: show partnerInterests (what partner has picked from your binder)
        let partnerPicksDisplay = [];

        if (trade?.isCardOwner) {
          // Card owner: trade.cards represents what requester wants from them
          // Convert trade.cards to display format
          const tradeCardsAsDisplay = (trade?.cards || [])
            .filter(c => c?.id)
            .map(c => ({
              id: `trade-${c.id}`,
              card_id: c.id,
              cards: { id: c.id, name: c.name, digital_image_url: c.digital_image_url },
              isTradeCard: true  // Flag to indicate this is from the original trade
            }));
          partnerPicksDisplay = tradeCardsAsDisplay;
        } else {
          // Requester: show partner's interests from your binder
          partnerPicksDisplay = partnerInterests;
        }

        return (
        <div style={styles.modalOverlay} onClick={() => setShowDetailsModal(false)}>
          <div style={styles.detailsModal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.detailsModalHeader}>
              <h3 style={styles.detailsModalTitle}>Trade Details</h3>
              <button
                style={styles.detailsCloseBtn}
                onClick={() => setShowDetailsModal(false)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Your Picks Section */}
            <div style={styles.interestsSection}>
              <div style={styles.interestsSectionLabel}>
                Your Picks from @{trade?.partnerName}
              </div>
              <div style={styles.interestsGrid}>
                {myInterests.map(item => (
                  <div key={item.id} style={styles.interestCard}>
                    <img
                      src={item.cards?.digital_image_url}
                      alt={item.cards?.name}
                      style={styles.interestCardImage}
                    />
                    <button
                      style={styles.removeInterestBtn}
                      onClick={() => removeInterest(item.id)}
                    >
                      <CloseIcon size={10} />
                    </button>
                  </div>
                ))}
                <button style={styles.addCardBtn} onClick={openBinderPicker}>
                  <span style={styles.addCardPlus}>+</span>
                  <span style={styles.addCardText}>Add</span>
                </button>
              </div>
              {myInterests.length >= 20 && (
                <div style={styles.warningBanner}>
                  You've selected 20+ cards - consider narrowing down your picks
                </div>
              )}
              <div style={styles.interestsCount}>
                {myInterests.length} card{myInterests.length !== 1 ? 's' : ''} selected
              </div>
            </div>

            {/* Partner's Picks Section */}
            <div style={styles.interestsSection}>
              <div style={styles.interestsSectionLabel}>
                @{trade?.partnerName}'s Picks from You
              </div>
              <div style={styles.interestsGrid}>
                {partnerPicksDisplay.length > 0 ? (
                  partnerPicksDisplay.map(item => (
                    <div key={item.id} style={styles.interestCard}>
                      <img
                        src={item.cards?.digital_image_url}
                        alt={item.cards?.name}
                        style={styles.interestCardImage}
                      />
                    </div>
                  ))
                ) : (
                  <span style={styles.emptyPicksText}>No picks yet</span>
                )}
              </div>
              {partnerPicksDisplay.length > 0 && (
                <div style={styles.interestsCount}>
                  {partnerPicksDisplay.length} card{partnerPicksDisplay.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={styles.detailsFooter}>
              These are just interests - nothing is final until you meet up
            </div>
          </div>
        </div>
        );
      })()}

      {/* Binder Picker Modal */}
      {showBinderPicker && (
        <div style={styles.modalOverlay}>
          <div style={styles.binderPickerModal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.binderPickerHeader}>
              <button
                style={styles.binderBackBtn}
                onClick={() => setShowBinderPicker(false)}
              >
                <ChevronLeftIcon />
              </button>
              <h3 style={styles.binderPickerTitle}>
                @{trade?.partnerName}'s Collection
              </h3>
              <button
                style={styles.binderDoneBtn}
                onClick={saveBinderSelections}
              >
                Done ({pendingSelections.length})
              </button>
            </div>

            {/* Search */}
            <div style={styles.binderSearchContainer}>
              <SearchIcon />
              <input
                style={styles.binderSearchInput}
                placeholder="Search cards..."
                value={binderSearch}
                onChange={(e) => setBinderSearch(e.target.value)}
              />
            </div>

            {/* Warning if 20+ */}
            {pendingSelections.length >= 20 && (
              <div style={styles.binderWarning}>
                You've selected 20+ cards
              </div>
            )}

            {/* Cards Grid */}
            <div style={styles.binderGrid}>
              {partnerCards
                .filter(card =>
                  !binderSearch ||
                  card.name?.toLowerCase().includes(binderSearch.toLowerCase())
                )
                .map(card => {
                  const isSelected = pendingSelections.includes(card.id);
                  return (
                    <div
                      key={card.id}
                      style={{
                        ...styles.binderCard,
                        ...(isSelected ? styles.binderCardSelected : {})
                      }}
                      onClick={() => toggleCardSelection(card.id)}
                    >
                      <img
                        src={card.digital_image_url}
                        alt={card.name}
                        style={styles.binderCardImage}
                      />
                      {isSelected && (
                        <div style={styles.binderCardCheck}>
                          <CheckIcon size={12} />
                        </div>
                      )}
                    </div>
                  );
                })}
              {partnerCards.length === 0 && (
                <div style={styles.emptyBinderText}>
                  No tradeable cards in their collection
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--bg)',
  },

  // Loading
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid var(--border)',
    borderTopColor: 'var(--accent)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
    backgroundColor: 'var(--bg)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--accent)',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  infoButton: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Status Banner
  statusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    backgroundColor: 'var(--bg-sheet)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  statusBannerBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: 'none',
    borderBottom: '1px solid var(--success)',
    width: '100%',
    cursor: 'pointer',
    textAlign: 'left',
  },
  statusTapHint: {
    fontSize: 12,
    color: 'var(--success)',
    fontWeight: 500,
  },
  statusIndicator: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statusPendingLocation: {
    backgroundColor: 'rgba(14, 165, 233, 0.2)',
    border: '1px solid var(--accent)',
  },
  statusPendingSchedule: {
    backgroundColor: 'rgba(168, 85, 247, 0.2)',
    border: '1px solid #a855f7',
  },
  statusPendingConfirm: {
    backgroundColor: 'rgba(249, 115, 22, 0.2)',
    border: '1px solid #f97316',
  },
  statusConfirmed: {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    border: '1px solid var(--success)',
  },
  statusStep: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text)',
  },
  statusText: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  statusProgress: {
    width: 60,
    height: 4,
    backgroundColor: 'var(--border)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  statusProgressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #0ea5e9, #22c55e)',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },

  // Messages Area
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Chase Message
  chaseMessageContainer: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
  },
  chaseAvatar: {
    width: 80,
    height: 120,
    objectFit: 'contain',
    flexShrink: 0,
  },
  chaseMessageBubble: {
    flex: 1,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 16,
    borderTopLeftRadius: 4,
    padding: 16,
    border: '2px solid #FF9800',
  },
  chaseName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#FF9800',
    marginBottom: 8,
    display: 'block',
  },
  chaseMessageText: {
    fontSize: 14,
    color: 'var(--text)',
    lineHeight: 1.5,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  confirmButtonsRow: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
  },
  confirmYesButton: {
    flex: 1,
    padding: '12px 20px',
    backgroundColor: 'var(--success)',
    border: 'none',
    borderRadius: 8,
    color: 'var(--bg-pure)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmNoButton: {
    flex: 1,
    padding: '12px 20px',
    backgroundColor: 'var(--border)',
    border: '1px solid var(--error)',
    borderRadius: 8,
    color: 'var(--error)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // User Message
  userMessageContainer: {
    display: 'flex',
  },
  userMessageRight: {
    justifyContent: 'flex-end',
  },
  userMessageLeft: {
    justifyContent: 'flex-start',
  },
  userMessageBubble: {
    maxWidth: '80%',
    borderRadius: 16,
    padding: 12,
  },
  userMessageBubbleMe: {
    backgroundColor: 'var(--accent)',
    borderBottomRightRadius: 4,
  },
  userMessageBubbleOther: {
    backgroundColor: 'var(--border)',
    borderBottomLeftRadius: 4,
  },
  userMessageText: {
    fontSize: 14,
    color: 'var(--text)',
    margin: 0,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
  },

  // Bottom Actions
  bottomActions: {
    display: 'flex',
    gap: 12,
    padding: '12px 20px',
    paddingBottom: 20,
    backgroundColor: 'var(--bg)',
  },
  actionBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: 12,
    color: 'var(--bg-pure)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnSecondary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    color: 'var(--text-muted)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  actionBtnCancel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: '1px solid var(--error, #ef4444)',
    borderRadius: 12,
    color: 'var(--error, #ef4444)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnWarn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: '1px solid var(--warning, #f59e0b)',
    borderRadius: 12,
    color: 'var(--warning, #f59e0b)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnGreen: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'var(--success)',
    border: 'none',
    borderRadius: 12,
    color: 'var(--bg-pure)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnDisabled: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'not-allowed',
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContentCenter: {
    width: '100%',
    maxWidth: 400,
    maxHeight: '80vh',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    gap: 12,
  },
  modalHeaderCenter: {
    flex: 1,
    textAlign: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
  },
  selectedCountSmall: {
    fontSize: 11,
    color: 'var(--text-secondary)',
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: 4,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtnSmall: {
    padding: '8px 16px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    color: 'var(--bg-pure)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtnSmallDisabled: {
    backgroundColor: 'var(--border)',
    color: 'var(--text-secondary)',
    cursor: 'not-allowed',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '12px 16px',
    padding: '10px 14px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    color: 'var(--text-secondary)',
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
  },
  locationFilterContainer: {
    display: 'flex',
    gap: 8,
    padding: '12px 16px 0',
  },
  locationFilterBtn: {
    flex: 1,
    padding: '10px 12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    color: 'var(--text-muted)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  locationFilterBtnActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
  },
  locationFilterBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  locationFilterBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    margin: '8px 16px 0',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderRadius: 8,
    color: 'var(--accent)',
    fontSize: 12,
  },
  locationList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 16px 20px 16px',
  },
  locationItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 0',
    borderBottom: '1px solid var(--border)',
  },
  locationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    marginRight: 12,
  },
  locationNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  locationName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  locationDistance: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--accent)',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    padding: '2px 6px',
    borderRadius: 4,
    whiteSpace: 'nowrap',
  },
  locationCity: {
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  locationMeta: {
    display: 'flex',
    gap: 12,
    marginTop: 4,
  },
  locationRating: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#ffd700',
  },
  locationSelectBtn: {
    padding: '8px 16px',
    backgroundColor: 'var(--border)',
    border: '1px solid var(--border-hover)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  locationSelectBtnActive: {
    backgroundColor: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: 'var(--bg-pure)',
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderTop: '1px solid var(--border)',
  },
  selectedCount: {
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  submitBtn: {
    padding: '12px 24px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: 10,
    color: 'var(--bg-pure)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    backgroundColor: 'var(--border)',
    color: 'var(--text-secondary)',
    cursor: 'not-allowed',
  },
  noResults: {
    textAlign: 'center',
    color: 'var(--text-secondary)',
    padding: 20,
  },

  // Suggest Location Link and Form
  suggestLocationLink: {
    background: 'none',
    border: 'none',
    color: 'var(--accent)',
    fontSize: 14,
    padding: '16px 0',
    textAlign: 'center',
    cursor: 'pointer',
    width: '100%',
    marginTop: 8,
  },
  suggestFormContainer: {
    padding: '16px 20px',
  },
  suggestFormDesc: {
    color: 'var(--text-muted)',
    fontSize: 14,
    marginBottom: 20,
    textAlign: 'center',
  },
  suggestFormGroup: {
    marginBottom: 16,
  },
  suggestFormRow: {
    display: 'flex',
    gap: 12,
  },
  suggestFormLabel: {
    display: 'block',
    color: 'var(--text-muted)',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  suggestFormInput: {
    width: '100%',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 14px',
    color: 'var(--text)',
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box',
  },
  suggestFormTextarea: {
    width: '100%',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 14px',
    color: 'var(--text)',
    fontSize: 16,
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  suggestSubmitBtn: {
    width: '100%',
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    padding: '14px',
    color: 'var(--text)',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  suggestSubmitBtnDisabled: {
    background: 'var(--border)',
    color: 'var(--text-secondary)',
    cursor: 'not-allowed',
  },
  suggestSuccessContainer: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  suggestSuccessIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'rgba(34, 197, 94, 0.2)',
    border: '2px solid var(--success)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    color: 'var(--success)',
  },
  suggestSuccessText: {
    color: 'var(--text-muted)',
    fontSize: 15,
    lineHeight: 1.5,
    marginBottom: 24,
  },
  suggestBackBtn: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 24px',
    color: 'var(--text)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Schedule Modal Styles
  selectAllRow: {
    display: 'flex',
    gap: 8,
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
  },
  selectAllBtn: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  selectAllBtnActive: {
    backgroundColor: 'var(--success)',
    borderColor: 'var(--success)',
    color: 'var(--bg-pure)',
  },
  clearAllBtn: {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: '1px solid var(--error)',
    borderRadius: 8,
    color: 'var(--error)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  timeSlotContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  selectAllDayBtn: {
    margin: '12px 16px 0 16px',
    padding: '10px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  selectAllDayBtnActive: {
    backgroundColor: 'var(--success)',
    borderColor: 'var(--success)',
    color: 'var(--bg-pure)',
  },
  dayTabs: {
    display: 'flex',
    gap: 8,
    padding: '12px 16px',
    overflowX: 'auto',
    borderBottom: '1px solid var(--border)',
  },
  dayTab: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '8px 12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    cursor: 'pointer',
    position: 'relative',
  },
  dayTabActive: {
    backgroundColor: 'var(--accent)',
    borderColor: 'var(--accent)',
  },
  dayTabDay: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text)',
  },
  dayTabDate: {
    fontSize: 10,
    color: 'var(--text-muted)',
  },
  dayTabBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'var(--success)',
    color: 'var(--bg-pure)',
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeSlotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    padding: 16,
    overflowY: 'auto',
    flex: 1,
  },
  timeSlotBtn: {
    padding: '12px 8px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
  },
  timeSlotBtnActive: {
    backgroundColor: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: 'var(--bg-pure)',
  },
  timeSlotBtnSelected: {
    backgroundColor: 'var(--success)',
    borderColor: 'var(--success)',
    color: 'var(--bg-pure)',
    fontWeight: 600,
  },
  selectDayPrompt: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: 'var(--text-secondary)',
    fontSize: 14,
    padding: 40,
  },

  // Choice List Styles
  choiceList: {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 8,
    overflowY: 'auto',
    flex: 1,
  },
  choiceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    cursor: 'pointer',
    color: 'var(--success)',
    transition: 'all 0.2s ease',
  },
  choiceItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    textAlign: 'left',
  },
  choiceItemName: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
  },
  choiceItemCity: {
    fontSize: 13,
    color: 'var(--text-muted)',
  },
  choiceItemSelected: {
    borderColor: 'var(--success)',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
  },
  radioCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    border: '2px solid var(--border-hover)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: 'var(--success)',
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: 'var(--success)',
  },

  // Trade Summary Modal Styles
  summaryModal: {
    width: '100%',
    maxWidth: 380,
    maxHeight: '85vh',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: 20,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid var(--border)',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 12px 16px',
    borderBottom: '1px solid var(--border)',
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  scheduledBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 16px',
    margin: '16px 20px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid var(--success)',
    borderRadius: 10,
    color: 'var(--success)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1,
  },
  usersRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: '8px 20px 20px 20px',
  },
  userCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  userAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '2px solid var(--border-hover)',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarInitial: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
    textAlign: 'center',
    maxWidth: 100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: 11,
    color: 'var(--text-secondary)',
  },
  withBadge: {
    padding: '6px 12px',
    borderRadius: 12,
    backgroundColor: 'var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--text-muted)',
    flexShrink: 0,
    letterSpacing: 0.5,
  },
  cardsSection: {
    padding: '0 20px 16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    margin: 0,
    marginBottom: 12,
  },
  cardsList: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    minWidth: 80,
  },
  cardThumb: {
    width: 70,
    height: 98,
    borderRadius: 6,
    objectFit: 'cover',
    backgroundColor: 'var(--border)',
    border: '1px solid var(--border-hover)',
  },
  cardName: {
    fontSize: 11,
    color: 'var(--text-muted)',
    textAlign: 'center',
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailsSection: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    color: 'var(--accent)',
  },
  detailInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  detailLabel: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailValue: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--text)',
  },
  detailSub: {
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  summaryFooter: {
    padding: '16px 20px 24px 20px',
    borderTop: '1px solid var(--border)',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 14,
    color: 'var(--success)',
    fontWeight: 500,
  },

  // Restart divider in chat
  restartDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    margin: '8px 0',
  },
  restartDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'var(--warning)',
    opacity: 0.4,
  },
  restartDividerText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--warning)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Restart button (subtle, next to status)
  restartBtn: {
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    backgroundColor: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    marginLeft: 'auto',
  },

  // Confirmation modal
  confirmModalContent: {
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: 16,
    padding: 24,
    maxWidth: 320,
    width: '90%',
    textAlign: 'center',
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: 12,
    marginTop: 0,
  },
  confirmText: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    marginBottom: 8,
  },
  confirmSubtext: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginBottom: 20,
  },
  confirmButtons: {
    display: 'flex',
    gap: 12,
  },
  confirmCancelBtn: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  confirmRestartBtn: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    border: 'none',
    backgroundColor: 'var(--error)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Expired Banner
  expiredBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    backgroundColor: 'rgba(136, 136, 136, 0.1)',
    borderBottom: '1px solid rgba(136, 136, 136, 0.3)',
  },
  expiredIndicator: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(136, 136, 136, 0.2)',
    border: '1px solid #888',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888',
    flexShrink: 0,
  },
  expiredContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  expiredTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#888',
  },
  expiredSubtext: {
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  viewCollectionBtn: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  // Details Button (header)
  detailsButton: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '6px 12px',
    color: 'var(--text)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },

  // Trade Details Modal
  detailsModal: {
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: 16,
    maxWidth: 380,
    width: '95%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  detailsModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  detailsModalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  detailsCloseBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Interests Section
  interestsSection: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  interestsSectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  interestsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
  },
  interestCard: {
    width: 60,
    height: 84,
    borderRadius: 6,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
  },
  interestCardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeInterestBtn: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  addCardBtn: {
    width: 60,
    height: 84,
    borderRadius: 6,
    border: '2px dashed var(--border)',
    backgroundColor: 'transparent',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  addCardPlus: {
    fontSize: 20,
    fontWeight: 300,
  },
  addCardText: {
    fontSize: 11,
    fontWeight: 500,
  },
  interestsCount: {
    marginTop: 12,
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  emptyPicksText: {
    fontSize: 13,
    color: 'var(--text-muted)',
    fontStyle: 'italic',
    padding: '20px 0',
  },
  warningBanner: {
    marginTop: 12,
    padding: '8px 12px',
    backgroundColor: 'rgba(234, 179, 8, 0.15)',
    borderRadius: 8,
    color: 'var(--warning)',
    fontSize: 12,
  },
  detailsFooter: {
    padding: '12px 20px',
    textAlign: 'center',
    fontSize: 12,
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },

  // Binder Picker Modal
  binderPickerModal: {
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: 16,
    width: '95%',
    maxWidth: 420,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  binderPickerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    gap: 12,
  },
  binderBackBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  binderPickerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
    textAlign: 'center',
  },
  binderDoneBtn: {
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    padding: '6px 12px',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  binderSearchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    color: 'var(--text-muted)',
  },
  binderSearchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
  },
  binderWarning: {
    margin: '0 16px',
    marginTop: 12,
    padding: '8px 12px',
    backgroundColor: 'rgba(234, 179, 8, 0.15)',
    borderRadius: 8,
    color: 'var(--warning)',
    fontSize: 12,
    textAlign: 'center',
  },
  binderGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
    gap: 8,
    padding: 16,
    overflow: 'auto',
    flex: 1,
  },
  binderCard: {
    aspectRatio: '2.5/3.5',
    borderRadius: 6,
    overflow: 'hidden',
    cursor: 'pointer',
    border: '2px solid transparent',
    position: 'relative',
    backgroundColor: 'var(--bg-card)',
  },
  binderCardSelected: {
    borderColor: 'var(--accent)',
  },
  binderCardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  binderCardCheck: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyBinderText: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: 14,
    padding: '40px 0',
  },
};

export default Chat;
